<div class="w-full max-w-6xl mx-auto p-6">
  @if (error()) {
    <div class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-2">
      <i class="pi pi-exclamation-triangle text-red-600"></i>
      <p class="text-red-800">{{ error() }}</p>
    </div>
  }

  @if (isSubscriptionInactive()) {
    <div class="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-center gap-2">
      <i class="pi pi-exclamation-circle text-amber-600"></i>
      <p class="text-amber-900">Esta suscripción está inactiva.</p>
    </div>
  }

  <div class="relative">
    @if (formDataLoading()) {
      <div
        class="absolute inset-0 z-40 flex items-start justify-center rounded-xl bg-slate-400/25 backdrop-blur-[2px] pt-24"
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <div class="flex flex-col items-center gap-3 rounded-2xl border border-slate-300/80 bg-white/95 px-8 py-6 shadow-lg">
          <i class="pi pi-spin pi-spinner text-3xl text-sky-600"></i>
          <p class="text-sm font-semibold text-slate-800">Cargando suscripción…</p>
        </div>
      </div>
    }

  <form [formGroup]="saleForm" (ngSubmit)="$event.preventDefault()">
    <p-card styleClass="sale-form-card">
      <ng-template pTemplate="header">
        <div class="px-4 py-3 border-b border-slate-200 bg-slate-50/50">
          <div class="flex items-center justify-between gap-3">
            <div class="flex items-center gap-3 min-w-0">
              <p-button
                icon="pi pi-arrow-left"
                [rounded]="true"
                [text]="true"
                severity="secondary"
                type="button"
                (onClick)="cancel()"
                pTooltip="Volver al listado"
                tooltipPosition="bottom"
              />
              <h2 class="text-lg font-semibold text-slate-900 flex items-center gap-2 min-w-0">
                <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                  <i class="pi pi-sync"></i>
                </span>
                <span class="truncate">{{ isEditMode() ? 'Editar suscripción' : 'Nueva suscripción' }}</span>
              </h2>
            </div>
            @if (editingSubscriptionId(); as subscriptionId) {
              <p-button
                icon="pi pi-refresh"
                [rounded]="true"
                [text]="true"
                severity="secondary"
                type="button"
                (onClick)="refreshSubscription()"
                [loading]="loading()"
                [disabled]="loading()"
                pTooltip="Actualizar suscripción"
                tooltipPosition="bottom"
              />
            }
          </div>
        </div>
        <div class="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50/90 px-3 py-2">
          <div class="flex min-w-0 flex-wrap items-center gap-2">
            <div class="inline-flex min-w-0 items-baseline gap-x-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5">
              <span class="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Folio</span>
              @if (editingSubscriptionId() == null) {
                <span class="text-sm font-medium font-mono italic text-slate-600">autogenerado</span>
              } @else {
                <span class="text-sm font-semibold tabular-nums tracking-tight text-slate-900">{{ folioDisplayValue() }}</span>
              }
            </div>
            <div
              class="inline-flex items-baseline gap-x-1.5 rounded-lg border bg-white px-2.5 py-1.5"
              [ngClass]="
                isSubscriptionCancelled()
                  ? 'border-rose-200 bg-rose-50'
                  : 'border-slate-200'
              "
            >
              <span
                class="text-[11px] font-semibold tracking-wider"
                [ngClass]="isSubscriptionCancelled() ? 'text-rose-600' : 'text-slate-500'"
              >STATUS:</span>
              <span
                class="text-sm font-medium font-mono"
                [ngClass]="isSubscriptionCancelled() ? 'text-rose-700' : 'text-slate-900'"
              >{{ statusDisplayLabel() }}</span>
              <i
                class="pi pi-question-circle cursor-help text-xs shrink-0"
                [ngClass]="isSubscriptionCancelled() ? 'text-rose-400' : 'text-slate-400'"
                [pTooltip]="statusTooltip"
                tooltipPosition="bottom"
              ></i>
            </div>
            @if (subscriptionPublicToken()) {
              <div class="inline-flex items-center gap-x-1.5 rounded-lg border border-teal-200 bg-teal-50 px-2.5 py-1.5">
                <i class="pi pi-link text-teal-600 text-xs"></i>
                <span class="text-[11px] font-semibold uppercase tracking-wider text-teal-700">Enlace de pago</span>
                <button
                  type="button"
                  class="inline-flex h-6 w-6 items-center justify-center rounded-md text-teal-700 transition hover:bg-teal-100 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-teal-400"
                  pTooltip="Copiar enlace"
                  tooltipPosition="bottom"
                  (click)="copySubscriptionPublicLink()"
                >
                  <i class="pi pi-copy text-xs"></i>
                </button>
                <button
                  type="button"
                  class="inline-flex h-6 w-6 items-center justify-center rounded-md text-teal-700 transition hover:bg-teal-100 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-teal-400"
                  pTooltip="Abrir"
                  tooltipPosition="bottom"
                  (click)="openSubscriptionPublicLink()"
                >
                  <i class="pi pi-external-link text-xs"></i>
                </button>
              </div>
            }
          </div>

          <div
            class="flex flex-wrap items-center justify-end gap-1.5"
            role="toolbar"
            aria-label="Acciones de la suscripción"
          >
            @if (editingSubscriptionId() && (editingSaleId() || canCancelSubscription())) {
              @if (editingSaleId()) {
                <span
                  class="inline-flex h-8 items-center [&_.p-splitbutton]:flex [&_.p-splitbutton]:h-8 [&_.p-splitbutton]:overflow-hidden [&_.p-splitbutton]:rounded-md [&_.p-splitbutton]:border [&_.p-splitbutton]:border-slate-200/80 [&_.p-splitbutton]:bg-white [&_.p-splitbutton]:shadow-sm [&_.p-splitbutton-button]:!rounded-none [&_.p-splitbutton-button]:!border-0 [&_.p-splitbutton-button]:!border-r [&_.p-splitbutton-button]:!border-slate-200/80 [&_.p-splitbutton-button]:!bg-white [&_.p-splitbutton-button]:!text-xs [&_.p-splitbutton-button]:!text-slate-700 [&_.p-splitbutton-button]:!shadow-none [&_.p-splitbutton-button:not(:disabled):hover]:!bg-emerald-50 [&_.p-splitbutton-dropdown]:!rounded-none [&_.p-splitbutton-dropdown]:!border-0 [&_.p-splitbutton-dropdown]:!bg-white [&_.p-splitbutton-dropdown]:!text-slate-700 [&_.p-splitbutton-dropdown]:!shadow-none [&_.p-splitbutton-dropdown]:!w-9 [&_.p-splitbutton-dropdown]:min-w-0 [&_.p-splitbutton-dropdown:not(:disabled):hover]:!bg-emerald-50 [&_.p-splitbutton_.p-button]:!py-1 [&_.p-splitbutton-button_.p-button-icon]:!text-sm [&_.p-splitbutton-button_.p-button-icon]:!text-emerald-600 [&_.p-splitbutton-button_.p-button-loading-icon]:!text-sm [&_.p-splitbutton-button_.p-button-loading-icon]:!text-emerald-600 [&_.p-splitbutton-button_.p-button-loading-icon_svg]:!h-4 [&_.p-splitbutton-button_.p-button-loading-icon_svg]:!w-4 [&_.p-splitbutton-button_.p-button-loading-icon_svg]:!text-emerald-600 [&_.p-splitbutton-dropdown]:!text-emerald-600 [&_.p-splitbutton-dropdown_svg]:!h-4 [&_.p-splitbutton-dropdown_svg]:!w-4 [&_.p-splitbutton-dropdown_svg]:fill-current"
                  pTooltip="Ir al recibo de pago del cliente."
                  tooltipPosition="bottom"
                >
                  <p-splitbutton
                    label="Ver Recibo"
                    icon="pi pi-receipt"
                    severity="secondary"
                    [outlined]="true"
                    size="small"
                    [model]="cobroSplitMenuItems()"
                    (onClick)="openFirstReceiptShortcut()"
                    [disabled]="loading()"
                    [loading]="chargesLoading() || receiptLinkCopyLoading()"
                    type="button"
                  />
                </span>
              }

              @if (canCancelSubscription()) {
                <button
                  type="button"
                  class="inline-flex h-8 items-center gap-1 rounded-md border border-slate-200/80 bg-white px-2 text-xs font-medium text-slate-700 shadow-sm transition hover:border-rose-300 hover:bg-rose-50 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-rose-400 disabled:pointer-events-none disabled:opacity-40"
                  (click)="confirmCancelSubscription()"
                  [disabled]="loading() || cancelSubscriptionLoading()"
                  pTooltip="Cancelar suscripción. No cancela ventas ni cargos pendientes."
                  tooltipPosition="bottom"
                >
                  <i
                    class="pi text-rose-600 text-sm"
                    [ngClass]="cancelSubscriptionLoading() ? 'pi-spinner pi-spin' : 'pi-ban'"
                  ></i>
                  <span class="hidden sm:inline">Cancelar suscripción</span>
                </button>
              }
            }
          </div>
        </div>
      </ng-template>
      <p-tabs [value]="activeTab()" (valueChange)="onTabChange($event)">
        <p-tablist>
          <p-tab value="general" class="flex items-center gap-2! text-inherit">
            <i class="pi pi-receipt"></i>
            <span>General</span>
          </p-tab>
          <p-tab
            value="sales"
            [disabled]="!editingSubscriptionId()"
            class="flex items-center gap-2! text-inherit"
            [class.opacity-50]="!editingSubscriptionId()"
            [class.pointer-events-none]="!editingSubscriptionId()"
          >
            <i class="pi pi-shopping-cart"></i>
            <span>Ventas</span>
          </p-tab>
          <p-tab
            value="auditoria"
            [disabled]="!editingSubscriptionId()"
            class="flex items-center gap-2! text-inherit"
            [class.opacity-50]="!editingSubscriptionId()"
            [class.pointer-events-none]="!editingSubscriptionId()"
          >
            <i class="pi pi-history"></i>
            <span>Auditoría</span>
          </p-tab>
        </p-tablist>
        <p-tabpanels>
          <p-tabpanel value="general">
            <div formGroupName="general">
            <div class="hidden" aria-hidden="true">
              <input type="text" formControlName="number" maxlength="20" tabindex="-1" />
            </div>
            <!-- Datos de la suscripción -->
            <div class="mb-8">
              <h3 class="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <i class="pi pi-file-edit"></i>
                Datos de la suscripción
              </h3>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div class="flex flex-col gap-1 md:col-span-2">
                  <div class="flex items-start gap-2">
                    <p-floatlabel class="w-full min-w-0 flex-1" variant="on">
                      <p-autocomplete
                        id="customer"
                        formControlName="customerSelection"
                        [suggestions]="customerOptions()"
                        (completeMethod)="onCustomerSearch($event)"
                        (onSelect)="onCustomerSelected($event.value)"
                        optionLabel="displayLabel"
                        placeholder="Busca por nombre comercial o legal (mín. 2 caracteres)"
                        [minLength]="2"
                        fluid
                        [disabled]="isReadonly() || isCustomerLocked()"
                        [invalid]="(generalForm.get('customerId')?.invalid ?? false) && (generalForm.get('customerId')?.touched ?? false)"
                      >
                        <ng-template #item let-customer>
                          <div class="flex flex-col py-0.5">
                            <span class="text-sm font-medium text-slate-900 leading-snug">{{ customer.comercialName }}</span>
                            <span class="text-xs text-slate-400 leading-snug">{{ customer.legalName }}</span>
                          </div>
                        </ng-template>
                      </p-autocomplete>
                      <label for="customer">Cliente <span class="text-red-500">*</span></label>
                    </p-floatlabel>
                    <p-button
                      type="button"
                      icon="pi pi-eye"
                      severity="secondary"
                      [outlined]="true"
                      [disabled]="!generalForm.get('customerId')?.value"
                      pTooltip="Ver cliente"
                      tooltipPosition="top"
                      styleClass="shrink-0"
                      (onClick)="openCustomerDetails()" />
                  </div>
                  <small class="text-slate-500">
                    @if (isCustomerLocked()) {
                      El cliente de la suscripción no se puede cambiar.
                    } @else {
                      Busca por nombre comercial o legal.
                    }
                  </small>
                  @if (generalForm.get('customerId')?.hasError('required') && generalForm.get('customerId')?.touched) {
                    <small class="text-red-600">El cliente es obligatorio.</small>
                  }
                </div>

                <div class="flex flex-col gap-1 md:col-span-2">
                  <p-floatlabel class="w-full" variant="on">
                    <p-autocomplete
                      id="planSelection"
                      formControlName="planSelection"
                      [suggestions]="planOptions()"
                      (completeMethod)="onPlanSearch($event)"
                      (onSelect)="onPlanSelected($event.value)"
                      (onClear)="onPlanClear()"
                      optionLabel="name"
                      placeholder="Busca un plan (mín. 2 caracteres)"
                      [minLength]="2"
                      [dropdown]="false"
                      [showClear]="!isExistingSubscriptionEdit()"
                      fluid
                      [disabled]="isSubscriptionImmutableFieldsLocked()"
                    >
                      <ng-template #item let-plan>
                        <div class="flex flex-col py-0.5">
                          <span class="text-sm font-medium text-slate-900 leading-snug">{{ plan.name }}</span>
                          @if (plan.description) {
                            <span class="text-xs text-slate-400 leading-snug">{{ plan.description }}</span>
                          }
                        </div>
                      </ng-template>
                      <ng-template #selectedItem let-plan>
                        <div class="flex flex-col py-0.5 min-w-0">
                          <span class="text-sm font-medium text-slate-900 leading-snug truncate">{{ plan.name }}</span>
                          @if (plan.description) {
                            <span class="text-xs text-slate-400 leading-snug truncate">{{ plan.description }}</span>
                          }
                        </div>
                      </ng-template>
                    </p-autocomplete>
                    <label for="planSelection">Plan</label>
                  </p-floatlabel>
                  <small class="text-slate-500">
                    @if (isExistingSubscriptionEdit()) {
                      El plan no se puede cambiar una vez creada la suscripción.
                    } @else {
                      Opcional. Si eliges un plan, se rellenan moneda, frecuencia, montos y líneas (quedan en solo lectura).
                    }
                  </small>
                </div>

                <div class="flex flex-col gap-1">
                  <p-floatlabel class="w-full" variant="on">
                    <div class="relative w-full">
                      <p-select
                        formControlName="collectionMethod"
                        inputId="collectionMethod"
                        [options]="collectionMethodOptions()"
                        optionLabel="label"
                        optionValue="value"
                        [disabled]="isCollectionMethodLocked()"
                        [invalid]="(generalForm.get('collectionMethod')?.invalid ?? false) && (generalForm.get('collectionMethod')?.touched ?? false)"
                        class="w-full pr-9"
                      />
                      <ng-template #collectionMethodTooltipGeneralTab>
                        <ul>
                          <li><strong>Automático</strong>: el cobro se realiza de forma automática según la configuración.<br/><br/></li>
                          <li><strong>Link de pago</strong>: genera un enlace de pago para que el cliente pague cuando lo desee.<br/><br/></li>
                          <li><strong>Manual</strong>: el cobro se gestiona manualmente fuera del sistema.<br/><br/></li>
                        </ul>
                      </ng-template>
                      <i
                        class="pi pi-info-circle text-slate-400 cursor-help absolute right-2 top-1/2 -translate-y-1/2 pointer-events-auto"
                        [pTooltip]="collectionMethodTooltipGeneralTab"
                        tooltipPosition="left"
                      ></i>
                    </div>
                    <label for="collectionMethod">Método de cobro <span class="text-red-500">*</span></label>
                  </p-floatlabel>
                  @if (generalForm.get('collectionMethod')?.hasError('required') && generalForm.get('collectionMethod')?.touched) {
                    <small class="text-red-600">El método de cobro es obligatorio.</small>
                  } @else {
                    <small class="text-slate-500">
                      @if (isExistingSubscriptionEdit() && currentSubscription()?.collectionMethod === CollectionMethodEnum.RECURRING_CHARGE) {
                        El método de cobro Cargo Recurrente no se puede cambiar una vez creada la suscripción.
                      } @else if (isExistingSubscriptionEdit()) {
                        Puedes cambiar entre Liga de pago y Manual; Cargo Recurrente no está disponible en edición.
                      } @else {
                        Selecciona el método de cobro para la suscripción.
                      }
                    </small>
                  }
                </div>

                <div class="flex flex-col gap-1">
                  <p-floatlabel class="w-full" variant="on">
                    <p-select
                      formControlName="currencyId"
                      inputId="currencyId"
                      [options]="currencySelectOptions()"
                      optionLabel="label"
                      optionValue="id"
                      class="w-full"
                      filter
                      [disabled]="isCommercialFieldsReadonly()"
                      [invalid]="(generalForm.get('currencyId')?.invalid ?? false) && (generalForm.get('currencyId')?.touched ?? false)"
                    />
                    <label for="currencyId">Moneda <span class="text-red-500">*</span></label>
                  </p-floatlabel>
                  @if (generalForm.get('currencyId')?.hasError('required') && generalForm.get('currencyId')?.touched) {
                    <small class="text-red-600">La moneda es obligatoria.</small>
                  } @else {
                    <small class="text-slate-500">La moneda se usará para calcular los totales de la suscripción.</small>
                  }
                </div>

                <div class="hidden" aria-hidden="true">
                  <input type="hidden" formControlName="processStatus" />
                  <input type="hidden" formControlName="status" />
                </div>

                <div class="flex flex-col  md:col-span-2">
                  <p-floatlabel class="w-full" variant="on">
                    <textarea
                      pTextarea
                      id="notes"
                      formControlName="notes"
                      rows="3"
                      [maxlength]="500"
                      fluid
                      autocomplete="off"
                      placeholder="Notas adicionales"
                      [disabled]="isReadonly()"
                      [readonly]="isReadonly()"
                    ></textarea>
                    <label for="notes">Notas</label>
                  </p-floatlabel>
                  @if (generalForm.get('notes')?.hasError('maxlength')) {
                    <small class="text-red-600">Máximo 500 caracteres.</small>
                  } @else {
                    <small class="text-slate-500">Las notas de uso interno.</small>
                  }
                </div>
              </div>
            </div>

            <!-- Términos de pago -->
            <div class="mt-8 border-t border-slate-200 pt-6" [formGroup]="paymentTermsForm">
              <div class="mb-4">
                <h3 class="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <i class="pi pi-wallet"></i>
                  Términos de pago
                </h3>
                <p class="text-sm text-slate-500">Define cuotas, periodicidad y plazos asociados a la suscripción.</p>
              </div>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div class="flex flex-col gap-1">
                  <p-floatlabel class="w-full" variant="on">
                    <p-select
                      formControlName="frequency"
                      inputId="frequency"
                      [options]="frequencySelectOptions()"
                      optionLabel="label"
                      optionValue="value"
                      optionDisabled="disabled"
                      class="w-full"
                      placeholder="Frecuencia"
                      [disabled]="isReadonly()"
                      (onChange)="onPaymentTermsCatchUpInput()"
                    />
                    <label for="frequency">Frecuencia</label>
                  </p-floatlabel>
                  <small class="text-slate-500">
                    Periodicidad de los pagos (mensual, trimestral, etc.).
                  </small>
                </div>
                <div class="flex flex-col gap-1">
                  <div class="flex w-full min-w-0 items-center gap-2">
                    <p-floatlabel class="min-w-0 flex-1" variant="on">
                      <p-datepicker
                        formControlName="collectionStartDate"
                        inputId="collectionStartDate"
                        [showTime]="false"
                        dateFormat="dd/mm/yy"
                        placeholder="dd/mm/aaaa"
                        fluid
                        [disabled]="isSubscriptionImmutableFieldsLocked()"
                        (onSelect)="onPaymentTermsCatchUpInput()"
                        (onClear)="onPaymentTermsCatchUpInput()"
                        (onInput)="onPaymentTermsCatchUpInput()"
                      />
                      <label for="collectionStartDate">Fecha inicio de cobro</label>
                    </p-floatlabel>
                    <ng-template #collectionStartDateTooltipPaymentTab>
                      <p>Fecha de inicio de cobro del ciclo. Si es una fecha pasada, se generarán ventas por cada ciclo completo vencido (desde ese ciclo hasta el anterior al ciclo en curso) y, al final, la venta del ciclo en curso. Puedes agrupar los ciclos vencidos en una sola venta. <br/><br/>Si se deja en blanco se toma la fecha actual al crear la suscripción.</p>
                    </ng-template>
                    <i
                      class="pi pi-info-circle text-slate-400 cursor-help shrink-0"
                      [pTooltip]="collectionStartDateTooltipPaymentTab"
                      tooltipPosition="left"
                    ></i>
                  </div>
                  <small class="text-slate-500">
                    @if (isExistingSubscriptionEdit()) {
                      La fecha de inicio de cobro no se puede modificar una vez creada la suscripción.
                    } @else {
                      Fecha en la que iniciarán los cobros de la primera venta.
                    }
                  </small>
                  @if (catchUpRequired()) {
                    <div class="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950">
                      <p class="font-medium flex items-center gap-2">
                        <i class="pi pi-exclamation-triangle text-amber-600"></i>
                        Se generarán ventas por ciclos completos atrasados
                      </p>
                      <p class="mt-1 text-amber-900/90">
                        La fecha de inicio de cobro es anterior a hoy. Se crearán
                        <span class="font-medium">{{ catchUpPreview().catchUpCycleCount }}</span>
                        venta{{ catchUpPreview().catchUpCycleCount === 1 ? '' : 's' }} por {{ catchUpPastCyclesDescription() }}
                        (nacidas como vencidas) y, al final, la venta del {{ catchUpCurrentCycleDescription() }}
                        @if (catchUpPreview().regularCycleLabel) {
                          (<span class="font-medium">{{ catchUpPreview().regularCycleLabel }}</span>).
                        } @else {
                          .
                        }
                      </p>
                      @if (catchUpPreview().catchUpCycleCount > 1) {
                        <label class="mt-3 flex cursor-pointer items-center gap-2 text-sm text-amber-950">
                          <p-checkbox
                            [binary]="true"
                            [ngModel]="groupCatchUpCycles()"
                            [ngModelOptions]="{ standalone: true }"
                            (ngModelChange)="onGroupCatchUpCyclesChange($event)"
                            inputId="groupCatchUpCycles"
                          />
                          <span>Agrupar todos los {{ catchUpPastCyclesDescription() }} en una sola venta</span>
                        </label>
                      }
                      <ul class="mt-3 max-h-44 overflow-y-auto divide-y divide-amber-200/70 rounded-md border border-amber-200/80 bg-white/70">
                        @if (groupCatchUpCycles()) {
                          @for (label of catchUpPreview().cycleLabels; track label) {
                            <li class="px-3 py-2 text-xs sm:text-sm text-amber-900/85">
                              {{ label }}
                            </li>
                          }
                        } @else {
                          @for (sale of catchUpPreviewSales(); track sale.cycleIndex) {
                            <li class="px-3 py-2 text-xs sm:text-sm text-amber-900/85">
                              {{ sale.cycleLabel }}
                            </li>
                          }
                        }
                      </ul>
                    </div>
                  }
                </div>
                <div class="flex flex-col gap-1">
                  <div class="flex w-full min-w-0 items-center gap-2">
                    <p-floatlabel class="min-w-0 flex-1" variant="on">
                      <p-inputNumber
                        id="billingDayOfMonth"
                        formControlName="billingDayOfMonth"
                        [min]="1"
                        [max]="31"
                        mode="decimal"
                        [minFractionDigits]="0"
                        [maxFractionDigits]="0"
                        fluid
                        placeholder=""
                        class="w-full"
                        (onInput)="onPaymentTermsCatchUpInput()"
                      />
                      <label for="billingDayOfMonth">Día de renovación</label>
                    </p-floatlabel>
                    <ng-template #billingDayOfMonthTooltipPaymentTab>
                      <p>Día del mes en que se renovará la suscripción (1–31). Si el mes no tiene ese día (por ejemplo el 30 en febrero), se usa el último día del mes. Si se omite, se toma el día de la fecha de inicio de cobro.</p>
                    </ng-template>
                    <i
                      class="pi pi-info-circle text-slate-400 cursor-help shrink-0"
                      [pTooltip]="billingDayOfMonthTooltipPaymentTab"
                      tooltipPosition="left"
                    ></i>
                  </div>
                  <small class="text-slate-500">Día del mes para la siguiente venta. Solo aplica a frecuencias mensual, trimestral y anual.</small>
                  @if (paymentTermsForm.get('billingDayOfMonth')?.hasError('min') || paymentTermsForm.get('billingDayOfMonth')?.hasError('max')) {
                    @if (paymentTermsForm.get('billingDayOfMonth')?.touched) {
                      <small class="text-red-600">Debe ser un día entre 1 y 31.</small>
                    }
                  }
                </div>
                <div class="flex flex-col gap-1">
                  <div class="flex w-full min-w-0 items-center gap-2">
                    <p-floatlabel class="min-w-0 flex-1" variant="on">
                      <p-inputNumber
                        id="daysUntilExpiry"
                        formControlName="daysUntilExpiry"
                        [min]="0"
                        mode="decimal"
                        [minFractionDigits]="0"
                        [maxFractionDigits]="0"
                        fluid
                        placeholder=""
                        [disabled]="isReadonly()"
                        class="w-full"
                      />
                      <label for="daysUntilExpiry">Plazo de pago</label>
                    </p-floatlabel>
                    <ng-template #daysUntilExpiryTooltipPaymentTab>
                      <p>Este campo indica el número de días que el cliente tiene para pagar la venta antes de que expire la vigencia del pago y sea considerado vencido. Si no se establece, la venta se considera vencida al día siguiente de la fecha de inicio de cobro.</p>
                    </ng-template>
                    <i
                      class="pi pi-info-circle text-slate-400 cursor-help shrink-0"
                      [pTooltip]="daysUntilExpiryTooltipPaymentTab"
                      tooltipPosition="left"
                    ></i>
                  </div>
                  <small class="text-slate-500 items-center flex gap-1"><i class="pi pi-wave-pulse text-amber-500 !text-sm" pTooltip="Este campo permite disparar un webhook o notificación para hacer una acción en consecuencia."></i> Días que tiene el cliente para pagar antes de vencer (<a href="#" target="_blank" class="text-sky-600 hover:text-sky-800">Leer más</a>).</small>
                </div>
              </div>
            </div>

            <!-- Facturación -->
            <div class="mt-8 border-t border-slate-200 pt-6" [formGroup]="invoicingForm">
              <div class="mb-4">
                <h3 class="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <i class="pi pi-file-check"></i>
                  Facturación
                </h3>
                <p class="text-sm text-slate-500">Datos fiscales opcionales (CFDI).</p>
              </div>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div class="flex flex-col gap-1">
                  <p-floatlabel class="w-full" variant="on">
                    <p-select
                      formControlName="paymentMethodId"
                      inputId="paymentMethodIdInvoicing"
                      [options]="paymentMethodOptions()"
                      optionLabel="description"
                      optionValue="id"
                      class="w-full"
                      filter
                      [invalid]="(invoicingForm.get('paymentMethodId')?.invalid ?? false) && (invoicingForm.get('paymentMethodId')?.touched ?? false)"
                    />
                    <label for="paymentMethodIdInvoicing">Método de pago @if (requiresAutoInvoiceFiscalFields()) {<span class="text-red-500">*</span>}</label>
                  </p-floatlabel>
                  @if (invoicingForm.get('paymentMethodId')?.hasError('required') && invoicingForm.get('paymentMethodId')?.touched) {
                    <small class="text-red-600">El método de pago es obligatorio.</small>
                  } @else {
                    <small class="text-slate-500">El método de utilizado para crear el comprobante CFDI.</small>
                  }
                </div>

                <div class="flex flex-col gap-1">
                  <p-floatlabel class="w-full" variant="on">
                    <p-select
                      formControlName="paymentFormId"
                      inputId="paymentFormIdInvoicing"
                      [options]="paymentFormOptions()"
                      optionLabel="description"
                      optionValue="id"
                      class="w-full"
                      filter
                      [showClear]="true"
                      [invalid]="(invoicingForm.get('paymentFormId')?.invalid ?? false) && (invoicingForm.get('paymentFormId')?.touched ?? false)"
                    />
                    <label for="paymentFormIdInvoicing">Forma de pago @if (isFiscalBillingType()) {<span class="text-red-500">*</span>}</label>
                  </p-floatlabel>
                  @if (invoicingForm.get('paymentFormId')?.hasError('required') && invoicingForm.get('paymentFormId')?.touched) {
                    <small class="text-red-600">La forma de pago es obligatoria.</small>
                  } @else {
                    <small class="text-slate-500">La forma de pago utilizada para crear el comprobante CFDI.</small>
                  }
                </div>

                <div class="flex flex-col gap-1">
                  <p-floatlabel class="w-full" variant="on">
                    <p-select
                      formControlName="cfdiUseId"
                      inputId="cfdiUseIdInvoicing"
                      [options]="satCfdiUseOptions()"
                      optionLabel="description"
                      optionValue="id"
                      class="w-full"
                      filter
                      [showClear]="true"
                      [invalid]="(invoicingForm.get('cfdiUseId')?.invalid ?? false) && (invoicingForm.get('cfdiUseId')?.touched ?? false)"
                    />
                    <label for="cfdiUseIdInvoicing">Uso de CFDI @if (requiresAutoInvoiceFiscalFields()) {<span class="text-red-500">*</span>}</label>
                  </p-floatlabel>
                  @if (invoicingForm.get('cfdiUseId')?.hasError('required') && invoicingForm.get('cfdiUseId')?.touched) {
                    <small class="text-red-600">El uso de CFDI es obligatorio.</small>
                  } @else {
                    <small class="text-slate-500">Requerido para facturas con valor fiscal (CFDI).</small>
                  }
                </div>

                <div class="flex flex-col gap-1">
                  <p-inputgroup class="w-full">
                    <div
                      class="flex items-center gap-3 flex-1 min-h-[2.75rem] px-3 border border-slate-300 rounded-md"
                      [class.bg-white]="canEditInvoicingPaymentAndCfdiFields()"
                      [class.bg-slate-100]="!canEditInvoicingPaymentAndCfdiFields()"
                    >
                      <p-checkbox
                        formControlName="autoInvoice"
                        inputId="autoInvoiceSubscription"
                        [binary]="true"
                      />
                      <label for="autoInvoiceSubscription" class="cursor-pointer select-none text-sm text-slate-700">
                        Generar factura automáticamente
                      </label>
                      <i
                        class="pi pi-info-circle text-slate-400 cursor-help ml-auto shrink-0"
                        pTooltip="Seleccionar esta opción provocará que se genere la factura de una venta automáticamente después de recibir el pago."
                        tooltipPosition="top"
                      ></i>
                    </div>
                  </p-inputgroup>
                  @if (autoInvoiceCustomerWarning(); as warning) {
                    <small class="text-red-600">{{ warning }}</small>
                  }
                </div>
              </div>
            </div>

            @if (dynamicFields().length) {
              <!-- Campos adicionales -->
              <div class="mt-8 border-t border-slate-200 pt-6" [formGroup]="additionalFieldsForm">
                <div class="mb-4">
                  <h3 class="text-lg font-semibold text-slate-900 flex items-center gap-2">
                    <i class="pi pi-sliders-h"></i>
                    Campos adicionales
                  </h3>
                  <p class="text-sm text-slate-500">Datos complementarios configurados para tu empresa.</p>
                </div>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  @for (field of dynamicFields(); track field.fieldKey) {
                    <div class="flex flex-col gap-1">
                      @if (field.type === 'BOOLEAN') {
                        <p-inputgroup class="w-full">
                          <div
                            class="flex items-center gap-3 flex-1 min-h-[2.75rem] px-3 border border-slate-300 rounded-l-md"
                            [class.bg-white]="!isReadonly()"
                            [class.bg-slate-100]="isReadonly()"
                          >
                            <p-checkbox
                              [formControlName]="field.fieldKey!"
                              [binary]="true"
                              [inputId]="'dyn_' + field.fieldKey"
                            />
                            <label [for]="'dyn_' + field.fieldKey" class="text-sm text-slate-700">
                              {{ field.label | titlecase }}
                              @if (field.required) {
                                <span class="text-red-500">*</span>
                              }
                            </label>
                          </div>
                          <p-inputgroup-addon>{{ fieldTypeLabel(field.type) }}</p-inputgroup-addon>
                        </p-inputgroup>
                      } @else if (field.type === 'NUMBER') {
                        <p-floatlabel class="w-full dyn-field-floatlabel" variant="on">
                          <p-inputgroup class="w-full">
                            <p-inputnumber
                              [formControlName]="field.fieldKey!"
                              [inputId]="'dyn_' + field.fieldKey"
                              class="w-full"
                              styleClass="w-full"
                              [useGrouping]="false"
                              [invalid]="(additionalFieldCtrl(field.fieldKey)?.invalid ?? false) && (additionalFieldCtrl(field.fieldKey)?.touched ?? false)"
                            />
                            <p-inputgroup-addon>{{ fieldTypeLabel(field.type) }}</p-inputgroup-addon>
                          </p-inputgroup>
                          <label [for]="'dyn_' + field.fieldKey">
                            {{ field.label | titlecase }}
                            @if (field.required) {
                              <span class="text-red-500">*</span>
                            }
                          </label>
                        </p-floatlabel>
                      } @else if (field.type === 'DATE') {
                        <p-floatlabel class="w-full dyn-field-floatlabel" variant="on">
                          <p-inputgroup class="w-full">
                            <p-datepicker
                              [formControlName]="field.fieldKey!"
                              [inputId]="'dyn_' + field.fieldKey"
                              dateFormat="dd/mm/yy"
                              [showIcon]="true"
                              class="w-full"
                              styleClass="w-full"
                              appendTo="body"
                              [invalid]="(additionalFieldCtrl(field.fieldKey)?.invalid ?? false) && (additionalFieldCtrl(field.fieldKey)?.touched ?? false)"
                            />
                            <p-inputgroup-addon>{{ fieldTypeLabel(field.type) }}</p-inputgroup-addon>
                          </p-inputgroup>
                          <label [for]="'dyn_' + field.fieldKey">
                            {{ field.label | titlecase }}
                            @if (field.required) {
                              <span class="text-red-500">*</span>
                            }
                          </label>
                        </p-floatlabel>
                      } @else if (field.type === 'SELECT') {
                        <p-floatlabel class="w-full dyn-field-floatlabel" variant="on">
                          <p-inputgroup class="w-full">
                            <p-select
                              [formControlName]="field.fieldKey!"
                              [inputId]="'dyn_' + field.fieldKey"
                              [options]="selectOptionsForField(field)"
                              optionLabel="label"
                              optionValue="value"
                              class="w-full"
                              [invalid]="(additionalFieldCtrl(field.fieldKey)?.invalid ?? false) && (additionalFieldCtrl(field.fieldKey)?.touched ?? false)"
                            />
                            <p-inputgroup-addon>{{ fieldTypeLabel(field.type) }}</p-inputgroup-addon>
                          </p-inputgroup>
                          <label [for]="'dyn_' + field.fieldKey">
                            {{ field.label | titlecase }}
                            @if (field.required) {
                              <span class="text-red-500">*</span>
                            }
                          </label>
                        </p-floatlabel>
                      } @else {
                        <p-floatlabel class="w-full dyn-field-floatlabel" variant="on">
                          <p-inputgroup class="w-full">
                            <input
                              pInputText
                              [formControlName]="field.fieldKey!"
                              [id]="'dyn_' + field.fieldKey"
                              class="w-full"
                              fluid
                              autocomplete="off"
                              [invalid]="(additionalFieldCtrl(field.fieldKey)?.invalid ?? false) && (additionalFieldCtrl(field.fieldKey)?.touched ?? false)"
                            />
                            <p-inputgroup-addon>{{ fieldTypeLabel(field.type) }}</p-inputgroup-addon>
                          </p-inputgroup>
                          <label [for]="'dyn_' + field.fieldKey">
                            {{ field.label | titlecase }}
                            @if (field.required) {
                              <span class="text-red-500">*</span>
                            }
                          </label>
                        </p-floatlabel>
                      }
                      @if (additionalFieldCtrl(field.fieldKey)?.hasError('required') && additionalFieldCtrl(field.fieldKey)?.touched) {
                        <small class="text-red-600">Este campo es obligatorio.</small>
                      } @else if (additionalFieldCtrl(field.fieldKey)?.hasError('pattern') && additionalFieldCtrl(field.fieldKey)?.touched) {
                        <small class="text-red-600">El valor no cumple el formato requerido.</small>
                      }
                    </div>
                  }
                </div>
              </div>
            }

            <!-- Líneas de la suscripción -->
            <div class="mt-8 border-t border-slate-200 pt-6">
              <div class="flex items-center justify-between mb-4">
                <div>
                  <h3 class="text-lg font-semibold text-slate-900 flex items-center gap-2">
                    <i class="pi pi-list"></i>
                    Líneas de la suscripción
                  </h3>
                  <p class="text-sm text-slate-500">Agrega productos o servicios a la suscripción.</p>
                </div>
                <p-button label="Agregar línea" icon="pi pi-plus" (onClick)="openLineModal('create')" type="button" [disabled]="isCommercialFieldsReadonly()" />
              </div>

              <div class="bg-white rounded-lg shadow-sm border border-slate-200 ">
                <div class="overflow-x-scroll">
                  <table class="w-full">
                    <thead class="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th class="w-10 px-2 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider"></th>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Producto</th>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Cantidad</th>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">P. unitario</th>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Subtotal</th>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Impuestos</th>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Total</th>
                        <th class="px-6 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">Acciones</th>
                      </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-slate-200">
                      @if (lines().length === 0) {
                        <tr>
                          <td class="px-6 py-6 text-center text-sm text-slate-500" colspan="9">
                            No hay líneas. Haz clic en "Agregar línea" para comenzar.
                          </td>
                        </tr>
                      }
                      @for (line of lines(); track line.tempId || line.id || $index) {
                        <tr class="hover:bg-slate-50 transition-colors">
                          <td class="w-10 px-2 py-4 align-top">
                            @if (line.taxes?.length) {
                              <button
                                type="button"
                                class="p-1.5 rounded text-slate-500 hover:bg-slate-200 hover:text-slate-700 transition-colors"
                                (click)="toggleLineExpanded($index)"
                                [attr.aria-expanded]="isLineExpanded($index)"
                              >
                                <i class="pi text-sm" [class.pi-chevron-right]="!isLineExpanded($index)" [class.pi-chevron-down]="isLineExpanded($index)"></i>
                              </button>
                            }
                          </td>
                          <td class="px-0 py-4 text-sm text-slate-600 max-w-xs">
                            <span
                              class="block truncate"
                              [pTooltip]="line.description || ''"
                              tooltipPosition="top"
                              appendTo="body"
                            >{{ line.description }}</span>
                          </td>
                          <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{{ line.quantity }}</td>
                          <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-600 text-right">{{ line.unitPrice | currency:'USD':'symbol':'1.2-2' }}</td>
                          <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-600 text-right">{{ line.subtotal | currency:'USD':'symbol':'1.2-2' }}</td>
                          <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-600 text-right">{{ line.taxAmount | currency:'USD':'symbol':'1.2-2' }}</td>
                          <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900 text-right">{{ line.totalAmount | currency:'USD':'symbol':'1.2-2' }}</td>
                          <td class="px-6 py-4 whitespace-nowrap text-right">
                            <div class="flex justify-end gap-1">
                              <p-button icon="pi pi-pen-to-square" [rounded]="true" [text]="true" severity="secondary" (onClick)="openLineModal('edit', $index)" type="button" [disabled]="isCommercialFieldsReadonly()" />
                              <p-button icon="pi pi-trash" [rounded]="true" [text]="true" severity="danger" (onClick)="confirmRemoveLine($index)" type="button" [disabled]="isCommercialFieldsReadonly() || lineRemoving()" [loading]="lineRemoving()" />
                            </div>
                          </td>
                        </tr>
                        @if (isLineExpanded($index) && line.taxes?.length) {
                          <tr class="bg-slate-50/70">
                            <td colspan="9" class="px-6 py-3">
                              <div class="pl-8 text-sm">
                                <div class="font-medium text-slate-600 mb-2">Desglose de impuestos</div>
                                <table class="w-full max-w-md border border-slate-200 rounded-lg overflow-hidden">
                                  <thead>
                                    <tr class="bg-slate-100 border-b border-slate-200">
                                      <th class="px-3 py-2 text-left text-xs font-medium text-slate-600">Código</th>
                                      <th class="px-3 py-2 text-left text-xs font-medium text-slate-600">Tasa</th>
                                      <th class="px-3 py-2 text-left text-xs font-medium text-slate-600 min-w-[5rem]">Tipo</th>
                                      <th class="px-3 py-2 text-right text-xs font-medium text-slate-600">Base</th>
                                      <th class="px-3 py-2 text-right text-xs font-medium text-slate-600">Monto</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    @for (t of line.taxes; track t.taxCode || $index) {
                                      <tr class="border-b border-slate-100 last:border-0">
                                        <td class="px-3 py-2 text-slate-700">{{ t.taxCode || '-' }}</td>
                                        <td class="px-3 py-2 text-slate-700">{{ formatTaxRate(t.taxRate) }}</td>
                                        <td class="px-3 py-2 text-slate-700">{{ getTaxCollectionTypeLabel(t.collectionType) || '-' }}</td>
                                        <td class="px-3 py-2 text-slate-700 text-right">{{ t.taxBase != null ? (t.taxBase | currency:'USD':'symbol':'1.2-2') : '-' }}</td>
                                        <td class="px-3 py-2 text-slate-700 text-right">{{ t.taxAmount != null ? (t.taxAmount | currency:'USD':'symbol':'1.2-2') : '-' }}</td>
                                      </tr>
                                    }
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        }
                      }
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <!-- Totales -->
            <!-- Inputs ocultos para que los controles estén vinculados y la validez del form se actualice al agregar líneas -->
            <div class="hidden">
              <input type="number" formControlName="subtotal" />
              <input type="number" formControlName="tax" />
              <input type="number" formControlName="traslados" />
              <input type="number" formControlName="retenciones" />
              <input type="number" formControlName="total" />
            </div>

            <div class="mt-8 border-t border-slate-200 pt-6">
              <!-- Inputs ocultos para que los controles de totales mantengan el binding y la validez del form se actualice -->
              <div class="hidden">
                <input type="number" formControlName="subtotal" />
                <input type="number" formControlName="tax" />
                <input type="number" formControlName="traslados" />
                <input type="number" formControlName="retenciones" />
                <input type="number" formControlName="total" />
              </div>
              <div class="p-4 bg-slate-50 rounded-lg border border-slate-200 max-w-sm ml-auto">
                <h4 class="text-sm font-semibold text-slate-700 mb-3">Totales</h4>
                <div class="space-y-2 text-sm">
                  <div class="flex justify-between text-slate-600">
                    <span>Subtotal</span>
                    <span>{{ totals().subtotal | number:'1.2-2' }} {{ currencyCode }}</span>
                  </div>
                  <div class="flex justify-between text-slate-600">
                    <span class="pl-2">Traslados</span>
                    <span>{{ totals().traslados | number:'1.2-2' }} {{ currencyCode }}</span>
                  </div>
                  <div class="flex justify-between text-slate-600">
                    <span class="pl-2">Retenciones</span>
                    <span>- {{ totals().retenciones | number:'1.2-2' }} {{ currencyCode }}</span>
                  </div>
                  <!--div class="flex justify-between text-slate-600">
                    <span>Impuestos</span>
                    <span>{{ totals().tax | number:'1.2-2' }} {{ currencyCode }}</span>
                  </div -->
                  <div class="flex justify-between font-semibold text-slate-900 pt-2 border-t border-slate-200">
                    <span>Total</span>
                    <span>{{ totals().total | number:'1.2-2' }} {{ currencyCode }}</span>
                  </div>
                </div>
              </div>
              @if (hasBillingImpact()) {
                <div class="mt-3 max-w-sm ml-auto rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950">
                  <p class="font-medium flex items-center gap-2">
                    <i class="pi pi-exclamation-triangle text-amber-600"></i>
                    Cambio con impacto en las ventas
                  </p>
                  <p class="mt-1 text-amber-900/90">
                    @if (billingCycleChangeRequired()) {
                      Como también cambiaste la frecuencia, al guardar podrás elegir si estos cambios aplican desde el
                      periodo actual o a partir del próximo cobro.
                    } @else {
                      Los cambios en productos o montos se reflejarán en la venta del cliente en el siguiente ciclo de cobro.
                    }
                  </p>
                </div>
              }
            </div>
            <!-- Controles ocultos para que el form actualice validez al cambiar totales -->
            <div class="hidden" aria-hidden="true">
              <input type="number" formControlName="subtotal" />
              <input type="number" formControlName="tax" />
              <input type="number" formControlName="traslados" />
              <input type="number" formControlName="retenciones" />
              <input type="number" formControlName="total" />
            </div>
            </div>
          </p-tabpanel>

          <p-tabpanel value="sales">
            <div class="py-4">
              <div class="flex items-center justify-between mb-4 gap-3 flex-wrap">
                <h3 class="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <i class="pi pi-shopping-cart"></i>
                  Ventas de la suscripción
                </h3>
                <div class="flex items-center gap-2">
                  <p-button
                    [label]="advanceActionLabel()"
                    icon="pi pi-forward"
                    (onClick)="openAdvanceCyclesDialog()"
                    type="button"
                    [disabled]="salesLoading() || advanceSubmitting()"
                    [pTooltip]="'Genera ventas adelantando ' + advanceCyclesUnitLabelLower() + ' de cobro'"
                    severity="secondary"
                    [outlined]="true"
                    [rounded]="true"
                  />
                  <p-button
                    label="Actualizar"
                    icon="pi pi-refresh"
                    (onClick)="loadSubscriptionSales()"
                    type="button"
                    [loading]="salesLoading()"
                    [disabled]="salesLoading()"
                    pTooltip="Actualiza la lista de ventas"
                    severity="secondary"
                    [rounded]="true"
                  />
                </div>
              </div>

              @if (salesLoading()) {
                <div class="flex items-center gap-2 text-slate-600 py-8">
                  <i class="pi pi-spin pi-spinner"></i>
                  <span>Cargando ventas...</span>
                </div>
              } @else if (!showSalesTable()) {
                <p class="text-slate-500 py-6">No hay ventas asociadas a esta suscripción.</p>
              } @else {
                <div class="rounded-lg border border-slate-200 overflow-hidden overflow-x-auto">
                  <table class="w-full min-w-[960px]">
                    <thead class="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th class="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase whitespace-nowrap">Venta</th>
                        <th class="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase whitespace-nowrap">Estado</th>
                        <th class="px-4 py-3 text-center text-xs font-semibold text-slate-700 uppercase whitespace-nowrap">F. venta</th>
                        <th class="px-4 py-3 text-center text-xs font-semibold text-slate-700 uppercase whitespace-nowrap">Vigencia</th>
                        <th class="px-4 py-3 text-center text-xs font-semibold text-slate-700 uppercase whitespace-nowrap hidden md:table-cell">F. emisión</th>
                        <th class="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase whitespace-nowrap">Saldo</th>
                        <th class="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase whitespace-nowrap">Total</th>
                        <th class="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase whitespace-nowrap">Acciones</th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-slate-200">
                      @if (nextScheduledSalePreview(); as nextSale) {
                        <tr class="bg-slate-50/80 border-b border-dashed border-slate-300">
                          <td class="px-4 py-3 whitespace-nowrap">
                            <div class="flex items-center gap-3">
                              <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-dashed border-slate-300 bg-white text-slate-400">
                                <i class="pi pi-calendar-clock text-sm"></i>
                              </div>
                              <div class="min-w-0">
                                <p class="text-sm font-medium text-slate-500 italic">Próxima venta</p>
                                <p class="text-xs text-slate-400">Programada automáticamente</p>
                              </div>
                            </div>
                          </td>
                          <td class="px-4 py-3 whitespace-nowrap">
                            <p-tag class="!text-xs" severity="info" value="Programada" />
                          </td>
                          <td class="px-4 py-3 text-sm text-slate-500 text-center whitespace-nowrap">
                            {{ nextSale.scheduledDate | date:'dd/MM/yyyy' }}
                          </td>
                          <td class="px-4 py-3 whitespace-nowrap">
                            @if (collectionValidity(nextSale.scheduledDate, collectionDueDate(nextSale.scheduledDate, nextSale.daysUntilExpiry)); as validity) {
                              <ng-container *ngTemplateOutlet="collectionValidityBar; context: { $implicit: validity }" />
                            } @else {
                              <span class="block text-center text-sm text-slate-400">—</span>
                            }
                          </td>
                          <td class="px-4 py-3 text-sm text-slate-400 text-center whitespace-nowrap hidden md:table-cell">—</td>
                          <td class="px-4 py-3 text-sm font-medium text-slate-500 text-right whitespace-nowrap">
                            {{ nextSale.total | currency:nextSale.currency:'symbol':'1.2-2' }}
                          </td>
                          <td class="px-4 py-3 text-sm font-medium text-slate-500 text-right whitespace-nowrap">
                            {{ nextSale.total | currency:nextSale.currency:'symbol':'1.2-2' }}
                          </td>
                          <td class="px-4 py-3 text-right whitespace-nowrap">
                            <span
                              class="inline-flex items-center gap-1.5 rounded-md border border-dashed border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-400"
                              pTooltip="Se generará al llegar la fecha de la próxima venta"
                              tooltipPosition="top"
                            >
                              <span>Pendiente</span>
                              <i class="pi pi-clock text-[10px]"></i>
                            </span>
                          </td>
                        </tr>
                      }
                      @for (sale of subscriptionSales(); track sale.id ?? $index) {
                        <tr class="hover:bg-slate-50">
                          <td class="px-4 py-3 whitespace-nowrap">
                            <div class="flex items-center gap-3">
                              <div [class]="getSaleStatusIconClasses(sale.status)">
                                <i [class]="getSaleStatusIcon(sale.status)"></i>
                              </div>
                              <div class="min-w-0">
                                <button
                                  type="button"
                                  class="text-sm font-medium text-sky-700 hover:text-sky-900 underline underline-offset-2 truncate block text-left max-w-full"
                                  (click)="openSaleDetails(sale.id!)"
                                >
                                  {{ sale.number || ('#' + sale.id) }}
                                </button>
                              </div>
                            </div>
                          </td>
                          <td class="px-4 py-3 whitespace-nowrap">
                            <p-tag class="!text-xs" [severity]="getSaleStatusSeverity(sale.status)" [value]="getSaleStatusLabel(sale.status)" />
                          </td>
                          <td class="px-4 py-3 text-sm text-slate-700 text-center whitespace-nowrap">
                            @if (sale.dateOfSale) {
                              {{ sale.dateOfSale | date:'dd/MM/yyyy' }}
                            } @else {
                              -
                            }
                          </td>
                          <td class="px-4 py-3 whitespace-nowrap">
                            @if (saleCollectionValidity(sale); as validity) {
                              <ng-container *ngTemplateOutlet="collectionValidityBar; context: { $implicit: validity }" />
                            } @else {
                              <span class="block text-center text-sm text-slate-700">-</span>
                            }
                          </td>
                          <td class="px-4 py-3 text-sm text-slate-700 text-center whitespace-nowrap hidden md:table-cell">
                            @if (sale.issueDate) {
                              <div class="flex flex-col items-center leading-tight">
                                <span>{{ sale.issueDate | date:'dd/MM/yyyy' }}</span>
                                <span class="text-xs text-slate-500">{{ sale.issueDate | date:'h:mm a' }}</span>
                              </div>
                            } @else {
                              -
                            }
                          </td>
                          <td class="px-4 py-3 text-sm font-medium text-slate-900 text-right whitespace-nowrap">
                            {{ (sale.amountDue ?? 0) | currency:(sale.currency || 'MXN'):'symbol':'1.2-2' }}
                          </td>
                          <td class="px-4 py-3 text-sm font-medium text-slate-900 text-right whitespace-nowrap">
                            {{ (sale.total ?? 0) | currency:(sale.currency || 'MXN'):'symbol':'1.2-2' }}
                          </td>
                          <td class="px-4 py-3 text-right whitespace-nowrap">
                            <a
                              [routerLink]="['/sales', sale.id]"
                              class="inline-flex items-center gap-1.5 rounded-md border border-sky-200 bg-sky-50 px-2.5 py-1.5 text-xs font-medium text-sky-800 transition hover:border-sky-300 hover:bg-sky-100"
                              pTooltip="Ver venta"
                              tooltipPosition="top"
                            >
                              <span>Ver</span>
                              <i class="pi pi-arrow-up-right text-[10px]"></i>
                            </a>
                          </td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
                <ng-template #collectionValidityBar let-validity>
                  <div class="flex min-w-[170px] flex-col gap-1">
                    <div class="flex items-center justify-between gap-3 text-xs" [class.text-slate-500]="!validity.expired" [class.text-red-600]="validity.expired">
                      <span>{{ validity.start | date:'dd/MM/yyyy' }}</span>
                      <span class="inline-flex items-center gap-1" [class.font-medium]="validity.expired">
                        <span>{{ validity.due | date:'dd/MM/yyyy' }}</span>
                        @if (validity.expired) {
                          <i
                            class="pi pi-exclamation-circle text-xs"
                            [pTooltip]="overdueTooltip(validity.due)"
                            tooltipPosition="top"
                          ></i>
                        }
                      </span>
                    </div>
                    <div
                      class="h-1.5 w-full overflow-hidden rounded-full bg-slate-200"
                      role="progressbar"
                      [attr.aria-valuenow]="validity.percent"
                      aria-valuemin="0"
                      aria-valuemax="100"
                      [pTooltip]="validity.remainingLabel"
                      tooltipPosition="top"
                    >
                      <div class="h-full rounded-full transition-all" [class]="validity.barClass" [style.width.%]="validity.percent"></div>
                    </div>
                  </div>
                </ng-template>
              }
            </div>
          </p-tabpanel>

          <p-tabpanel value="auditoria">
            @if (activeTab() === 'auditoria') {
              <app-entity-audit-panel
                entityType="Subscription"
                [entityId]="editingSubscriptionId()"
                entityLabel="suscripción"
              />
            }
          </p-tabpanel>
        </p-tabpanels>
      </p-tabs>

      <ng-template pTemplate="footer">
        <div class="!mt-4 flex justify-end gap-4 mt-1 !p-4 !-m-5 bg-gray-50 p-2 border-t border-slate-200">
          <p-button label="Cancelar" severity="secondary" (onClick)="cancel()" type="button" />
          @if (showInvoicingProfileUpdateInFooter()) {
            <p-button
              label="Actualizar perfil de facturación"
              icon="pi pi-save"
              type="button"
              (onClick)="submitInvoicingProfileUpdate()"
              [loading]="invoicingProfileUpdateLoading()"
              [disabled]="loading() || invoicingProfileUpdateLoading() || !invoicingProfileSubsetDirty()"
            />
          }
          @if (!saleIsClosedOperationalFooter()) {
            <div class="flex flex-wrap items-center justify-end gap-3">
              @if (isEditMode()) {
                <p-button
                  label="Guardar"
                  icon="pi pi-save"
                  (onClick)="onSubmitWithStatus('ISSUED')"
                  [loading]="loading()"
                  [disabled]="loading() || isReadonly() || !footerDraftSubmitEnabled()"
                  type="button"
                />
              } @else {
                <p-button
                  label="Crear suscripción"
                  icon="pi pi-check-circle"
                  (onClick)="confirmCloseSale()"
                  [disabled]="loading() || isReadonly() || !footerDraftSubmitEnabled()"
                  [loading]="loading()"
                  type="button"
                />
              }
            </div>
          }
        </div>
      </ng-template>
    </p-card>
  </form>
  </div>
</div>

<p-dialog
  [visible]="closeSaleConfirmVisible()"
  (visibleChange)="closeSaleConfirmVisible.set($event)"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [dismissableMask]="true"
  [closable]="true"
  appendTo="body"
  [style]="{ width: 'min(26rem, calc(100vw - 2rem))' }"
  styleClass="sale-close-confirm-dialog"
  contentStyleClass="!p-0 !overflow-hidden rounded-xl"
  [focusOnShow]="true"
>
  <ng-template #header>
    <div class="flex w-full min-w-0 items-start gap-4 pr-2">
      <div
        class="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-100 to-orange-50 text-amber-700 shadow-inner ring-1 ring-amber-200/60"
        aria-hidden="true"
      >
        <i class="pi pi-exclamation-triangle text-xl"></i>
      </div>
      <div class="min-w-0 flex-1 pt-0.5">
        <h2 class="text-lg font-semibold leading-snug tracking-tight text-slate-900">Crear suscripción</h2>
        <p class="mt-1 text-sm font-normal text-slate-500">Se iniciará el flujo de cobro al cliente</p>
      </div>
    </div>
  </ng-template>

  <div class="border-t border-slate-100 bg-white px-6 py-5">
    <p class="text-sm leading-relaxed text-slate-600">
      Vas a <span class="font-medium text-slate-800">crear la suscripción</span> y se
      habilitará el proceso de <span class="font-medium text-slate-800">cobro al cliente</span> según los términos y el
      método de cobro configurados.
    </p>
    <ul class="mt-4 space-y-2.5 text-sm text-slate-700">
      <li class="flex gap-3">
        <span
          class="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500"
          aria-hidden="true"
        >
          <i class="pi pi-credit-card text-xs"></i>
        </span>
        <span>Se procederá a programar los pagos según los términos de pago configurados.</span>
      </li>
    </ul>
  </div>

  <ng-template #footer>
    <div
      class="sale-close-confirm-dialog-footer flex w-full min-w-full max-w-none flex-wrap items-center justify-end gap-2 border-t border-slate-200/80 bg-slate-50 px-5 py-4"
    >
      <p-button
        label="Cancelar"
        severity="secondary"
        [outlined]="true"
        type="button"
        (onClick)="cancelCloseSaleConfirm()"
      />
      <p-button
        label="Sí, crear suscripción"
        icon="pi pi-check"
        severity="success"
        type="button"
        styleClass="!bg-emerald-600 !border-emerald-600 hover:!bg-emerald-700 hover:!border-emerald-700"
        (onClick)="confirmCloseSaleSubmit()"
      />
    </div>
  </ng-template>
</p-dialog>

<p-dialog
  [visible]="stampSaleConfirmVisible()"
  (visibleChange)="stampSaleConfirmVisible.set($event)"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [dismissableMask]="true"
  [closable]="true"
  appendTo="body"
  [style]="{ width: 'min(26rem, calc(100vw - 2rem))' }"
  styleClass="sale-stamp-confirm-dialog"
  contentStyleClass="!p-0 !overflow-hidden rounded-xl"
  [focusOnShow]="true"
>
  <ng-template #header>
    <div class="flex w-full min-w-0 items-start gap-4 pr-2">
      <div
        class="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-100 to-slate-50 text-sky-700 shadow-inner ring-1 ring-sky-200/60"
        aria-hidden="true"
      >
        <i class="pi pi-file-export text-xl"></i>
      </div>
      <div class="min-w-0 flex-1 pt-0.5">
        <h2 class="text-lg font-semibold leading-snug tracking-tight text-slate-900">Facturar (timbrar CFDI)</h2>
        <p class="mt-1 text-sm font-normal text-slate-500">Emisión fiscal ante el SAT</p>
      </div>
    </div>
  </ng-template>

  <div class="border-t border-slate-100 bg-white px-6 py-5">
    <p class="text-sm leading-relaxed text-slate-600">
      Vas a <span class="font-medium text-slate-800">generar y timbrar el comprobante fiscal (CFDI)</span> asociado a esta
      suscripción. El PAC validará los datos; una vez timbrado, el XML y el PDF quedarán disponibles para descarga.
    </p>
    <ul class="mt-4 space-y-2.5 text-sm text-slate-700">
      <li class="flex gap-3">
        <span
          class="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500"
          aria-hidden="true"
        >
          <i class="pi pi-check-square text-xs"></i>
        </span>
        <span>Revisa en Facturación que método de pago, forma de pago y uso de CFDI sean correctos.</span>
      </li>
      <li class="flex gap-3">
        <span
          class="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500"
          aria-hidden="true"
        >
          <i class="pi pi-lock text-xs"></i>
        </span>
        <span>El timbrado tiene efectos fiscales; una vez emitida, no se puede modificar.</span>
      </li>
    </ul>
  </div>

  <ng-template #footer>
    <div
      class="sale-close-confirm-dialog-footer flex w-full min-w-full max-w-none flex-wrap items-center justify-end gap-2 border-t border-slate-200/80 bg-slate-50 px-5 py-4"
    >
      <p-button
        label="Cancelar"
        severity="secondary"
        [outlined]="true"
        type="button"
        [disabled]="stampSaleLoading()"
        (onClick)="cancelStampSaleConfirm()"
      />
      <p-button
        label="Sí, facturar"
        icon="pi pi-check"
        severity="success"
        type="button"
        styleClass="!bg-emerald-600 !border-emerald-600 hover:!bg-emerald-700 hover:!border-emerald-700"
        [loading]="stampSaleLoading()"
        [disabled]="stampSaleLoading()"
        (onClick)="executeStampSale()"
      />
    </div>
  </ng-template>
</p-dialog>

<p-dialog
  [visible]="cancelSubscriptionConfirmVisible()"
  (visibleChange)="!$event ? cancelCancelSubscriptionConfirm() : cancelSubscriptionConfirmVisible.set($event)"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [dismissableMask]="!cancelSubscriptionLoading()"
  [closable]="!cancelSubscriptionLoading()"
  appendTo="body"
  [style]="{ width: 'min(26rem, calc(100vw - 2rem))' }"
  styleClass="sale-close-confirm-dialog"
  contentStyleClass="!p-0 !overflow-hidden rounded-xl"
  [focusOnShow]="true"
>
  <ng-template #header>
    <div class="flex w-full min-w-0 items-start gap-4 pr-2">
      <div
        class="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-rose-100 to-orange-50 text-rose-700 shadow-inner ring-1 ring-rose-200/60"
        aria-hidden="true"
      >
        <i class="pi pi-ban text-xl"></i>
      </div>
      <div class="min-w-0 flex-1 pt-0.5">
        <h2 class="text-lg font-semibold leading-snug tracking-tight text-slate-900">Cancelar suscripción</h2>
        <p class="mt-1 text-sm font-normal text-slate-500">Detiene las renovaciones futuras</p>
      </div>
    </div>
  </ng-template>

  <div class="border-t border-slate-100 bg-white px-6 py-5">
    <p class="text-sm leading-relaxed text-slate-600">
      Vas a <span class="font-medium text-slate-800">cancelar esta suscripción</span>. Dejará de renovarse y no se
      generarán nuevas ventas ni cargos programados.
    </p>
    <ul class="mt-4 space-y-2.5 text-sm text-slate-700">
      <li class="flex gap-3">
        <span
          class="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500"
          aria-hidden="true"
        >
          <i class="pi pi-shopping-cart text-xs"></i>
        </span>
        <span>Las ventas existentes <span class="font-medium text-slate-800">no se cancelan</span>.</span>
      </li>
      <li class="flex gap-3">
        <span
          class="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500"
          aria-hidden="true"
        >
          <i class="pi pi-credit-card text-xs"></i>
        </span>
        <span>Los cargos pendientes <span class="font-medium text-slate-800">permanecen activos</span>.</span>
      </li>
    </ul>
  </div>

  <ng-template #footer>
    <div
      class="sale-close-confirm-dialog-footer flex w-full min-w-full max-w-none flex-wrap items-center justify-end gap-2 border-t border-slate-200/80 bg-slate-50 px-5 py-4"
    >
      <p-button
        label="Volver"
        severity="secondary"
        [outlined]="true"
        type="button"
        [disabled]="cancelSubscriptionLoading()"
        (onClick)="cancelCancelSubscriptionConfirm()"
      />
      <p-button
        label="Sí, cancelar suscripción"
        icon="pi pi-ban"
        severity="danger"
        type="button"
        [loading]="cancelSubscriptionLoading()"
        [disabled]="cancelSubscriptionLoading()"
        (onClick)="executeCancelSubscription()"
      />
    </div>
  </ng-template>
</p-dialog>

<p-dialog
  [visible]="cfdiPdfViewerVisible()"
  (visibleChange)="onCfdiPdfViewerVisibleChange($event)"
  [modal]="true"
  [draggable]="false"
  [resizable]="true"
  [dismissableMask]="true"
  [closable]="true"
  appendTo="body"
  [style]="{ width: 'min(56rem, calc(100vw - 1.5rem))' }"
  [contentStyle]="{ padding: '0' }"
  styleClass="cfdi-pdf-viewer-dialog"
  header="CFDI — vista PDF"
  [focusOnShow]="true"
>
  @if (cfdiPdfViewerSafeUrl(); as pdfSrc) {
    <iframe
      [src]="pdfSrc"
      class="block min-h-[72vh] w-full rounded-b-lg border-0 bg-slate-100"
      title="Vista previa del PDF del CFDI"
    ></iframe>
  } @else {
    <div class="flex min-h-[12rem] items-center justify-center text-slate-500">
      <i class="pi pi-spin pi-spinner mr-2"></i>
      <span>Cargando vista previa…</span>
    </div>
  }
</p-dialog>

<p-dialog
  [visible]="recallEmailDialogVisible()"
  (visibleChange)="!$event ? closeRecallEmailDialog() : recallEmailDialogVisible.set($event)"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [dismissableMask]="!recallLoading()"
  [closable]="!recallLoading()"
  appendTo="body"
  [style]="{ width: 'min(28rem, calc(100vw - 2rem))' }"
  header="Enviar liga de pago"
>
  <div class="flex flex-col gap-4 pt-1">
    @if (recallConvertsManualCharge()) {
      <div class="bg-amber-50 border border-amber-200 rounded-lg p-3 flex gap-2">
        <i class="pi pi-exclamation-triangle text-amber-600 mt-0.5 shrink-0"></i>
        <p class="text-sm text-amber-900 m-0">
          Esta es una venta con cobro <strong>manual</strong>. Al enviar, el recibo pasará a
          <strong>liga de pago</strong> y se generará el enlace para el cliente.
        </p>
      </div>
    }
    <p class="text-sm text-slate-600 m-0">
      Indica el correo al que se enviará el enlace de pago del recibo.
    </p>
    <p-floatlabel variant="on">
      <input
        pInputText
        id="recall-email"
        class="w-full"
        type="email"
        autocomplete="email"
        [ngModel]="recallEmail()"
        (ngModelChange)="recallEmail.set($event)"
        [disabled]="recallLoading()"
      />
      <label for="recall-email">Correo electrónico</label>
    </p-floatlabel>
  </div>
  <ng-template #footer>
    <div class="flex justify-end gap-2">
      <p-button
        label="Cancelar"
        severity="secondary"
        [text]="true"
        type="button"
        [disabled]="recallLoading()"
        (onClick)="closeRecallEmailDialog()"
      />
      <p-button
        label="Enviar"
        icon="pi pi-send"
        type="button"
        [loading]="recallLoading()"
        [disabled]="recallLoading() || !recallEmail().trim()"
        (onClick)="submitRecallEmail()"
      />
    </div>
  </ng-template>
</p-dialog>

<p-dialog
  [visible]="advanceCyclesDialogVisible()"
  (visibleChange)="!$event ? closeAdvanceCyclesDialog() : advanceCyclesDialogVisible.set($event)"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [dismissableMask]="!advanceSubmitting()"
  [closable]="!advanceSubmitting()"
  appendTo="body"
  [style]="{ width: 'min(34rem, calc(100vw - 2rem))' }"
  [header]="advanceActionLabel()"
>
  <div class="flex flex-col gap-4 pt-1">
    <p class="text-sm text-slate-600 m-0">
      Genera ventas por adelantado y mueve la próxima venta programada para no volver a cobrar esos {{ advanceCyclesUnitLabelLower() }}.
    </p>

    <p-floatlabel variant="on" class="w-full">
      <p-inputgroup class="w-full">
        <p-inputnumber
          inputId="advance-cycles"
          class="w-full"
          [ngModel]="advanceCycles()"
          (ngModelChange)="onAdvanceCyclesChange($event)"
          [min]="1"
          [max]="36"
          [showButtons]="true"
          [disabled]="advanceSubmitting()"
        />
        <p-inputgroup-addon>{{ advanceCyclesUnitLabel() }}</p-inputgroup-addon>
      </p-inputgroup>
      <label for="advance-cycles">Número de ciclos</label>
    </p-floatlabel>

    <div class="flex items-center gap-2">
      <p-checkbox
        inputId="advance-group-cycles"
        [binary]="true"
        [ngModel]="advanceGroupCycles()"
        (ngModelChange)="onAdvanceGroupCyclesChange($event)"
        [disabled]="advanceSubmitting()"
      />
      <label for="advance-group-cycles" class="cursor-pointer select-none">
        Agrupar {{ advanceCyclesUnitLabelLower() }} en una sola venta
      </label>
    </div>

    <div class="rounded-lg border border-slate-200 overflow-hidden">
      <div class="bg-slate-50 px-3 py-2 border-b border-slate-200 flex items-center justify-between gap-2">
        <span class="text-xs font-semibold uppercase tracking-wide text-slate-600">Simulación de ventas</span>
        @if (advancePreviewLoading()) {
          <span class="text-xs text-slate-500 inline-flex items-center gap-1">
            <i class="pi pi-spin pi-spinner text-[10px]"></i>
            Calculando…
          </span>
        }
      </div>

      @if (advancePreviewSales().length === 0 && !advancePreviewLoading()) {
        <p class="text-sm text-slate-500 px-3 py-4 m-0">Ajusta los parámetros para ver el resumen.</p>
      } @else if (advancePreviewSales().length > 0) {
        <ul class="divide-y divide-slate-100 m-0 p-0 list-none">
          @for (sale of advancePreviewSales(); track $index) {
            <li class="px-3 py-2.5 flex items-center justify-between gap-3">
              <div class="min-w-0">
                <p class="text-sm font-medium text-slate-800 m-0 truncate">
                  @if (advanceGroupCycles() && (sale.cyclesIncluded ?? 0) > 1) {
                    Venta agrupada ({{ sale.cyclesIncluded }} {{ advanceCyclesUnitForCount(sale.cyclesIncluded ?? 0) }})
                  } @else {
                    {{ advanceCyclesUnitSingularLabel() }} {{ sale.cycleIndex ?? ($index + 1) }}
                  }
                </p>
                @if (sale.billingDate) {
                  <p class="text-xs text-slate-500 m-0 mt-0.5">
                    Venta: {{ formatApiDateOnly(sale.billingDate) }}
                  </p>
                }
              </div>
              <div class="text-right shrink-0">
                <p class="text-sm font-semibold text-slate-900 m-0 tabular-nums">
                  {{ (sale.total ?? 0) | currency:(sale.currency || 'MXN'):'symbol':'1.2-2' }}
                </p>
                <p class="text-xs text-slate-500 m-0 tabular-nums">
                  Impuestos {{ (sale.tax ?? 0) | currency:(sale.currency || 'MXN'):'symbol':'1.2-2' }}
                </p>
              </div>
            </li>
          }
        </ul>
        <div class="border-t border-slate-200 bg-slate-50 px-3 py-2.5 space-y-1">
          <div class="flex justify-between text-sm">
            <span class="text-slate-600">Total a generar</span>
            <span class="font-semibold text-slate-900 tabular-nums">
              {{ advancePreviewTotal() | currency:(advancePreview()?.sales?.[0]?.currency || 'MXN'):'symbol':'1.2-2' }}
            </span>
          </div>
          @if (advancePreview()?.previousNextBillingDate || advancePreview()?.nextBillingDate) {
            <div class="flex justify-between text-xs text-slate-500 gap-2">
              <span>Próxima venta</span>
              <span class="text-right">
                @if (advancePreview()?.previousNextBillingDate) {
                  {{ formatApiDateOnly(advancePreview()!.previousNextBillingDate) }}
                  →
                }
                {{ formatApiDateOnly(advancePreview()?.nextBillingDate) }}
              </span>
            </div>
          }
        </div>
      }
    </div>
  </div>

  <ng-template #footer>
    <div class="flex justify-end gap-2">
      <p-button
        label="Cancelar"
        severity="secondary"
        [text]="true"
        type="button"
        [disabled]="advanceSubmitting()"
        (onClick)="closeAdvanceCyclesDialog()"
      />
      <p-button
        label="Generar Venta/s" 
        icon="pi pi-forward"
        type="button"
        [disabled]="advanceSubmitting() || advancePreviewLoading() || advancePreviewSales().length === 0"
        (onClick)="submitAdvanceCycles()"
      />
    </div>
  </ng-template>
</p-dialog>

<p-dialog
  [visible]="advanceCyclesConfirmVisible()"
  (visibleChange)="!$event ? cancelAdvanceCyclesConfirm() : advanceCyclesConfirmVisible.set($event)"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [dismissableMask]="!advanceSubmitting()"
  [closable]="!advanceSubmitting()"
  appendTo="body"
  [style]="{ width: 'min(28rem, calc(100vw - 2rem))' }"
  styleClass="sale-close-confirm-dialog"
  contentStyleClass="!p-0 !overflow-hidden rounded-xl"
  [focusOnShow]="true"
>
  <ng-template #header>
    <div class="flex w-full min-w-0 items-start gap-4 pr-2">
      <div
        class="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-100 to-orange-50 text-amber-700 shadow-inner ring-1 ring-amber-200/60"
        aria-hidden="true"
      >
        <i class="pi pi-exclamation-triangle text-xl"></i>
      </div>
      <div class="min-w-0 flex-1 pt-0.5">
        <h2 class="text-lg font-semibold leading-snug tracking-tight text-slate-900">{{ advanceActionLabel() }}</h2>
        <p class="mt-1 text-sm font-normal text-slate-500">Este ajuste es irreversible</p>
      </div>
    </div>
  </ng-template>

  <div class="border-t border-slate-100 bg-white px-6 py-5">
    <p class="text-sm leading-relaxed text-slate-600">
      Vas a adelantar
      <span class="font-medium text-slate-800">
        {{ advanceCycles() }} {{ advanceCyclesUnitForCount(advanceCycles()) }}
      </span>
      de cobro.
    </p>
    <ul class="mt-4 space-y-2.5 text-sm text-slate-700">
      <li class="flex gap-3">
        <span
          class="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500"
          aria-hidden="true"
        >
          <i class="pi pi-shopping-cart text-xs"></i>
        </span>
        @if (advanceGroupCycles()) {
          <span>
            Se creará <span class="font-medium text-slate-800">una sola venta</span>
            con los productos multiplicados por {{ advanceCycles() }}.
          </span>
        } @else {
          <span>
            Se crearán <span class="font-medium text-slate-800">{{ advanceCycles() }} venta{{ advanceCycles() === 1 ? '' : 's' }}</span>
            (una por {{ advanceCyclesUnitForCount(1) }}).
          </span>
        }
      </li>
      <li class="flex gap-3">
        <span
          class="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500"
          aria-hidden="true"
        >
          <i class="pi pi-calendar text-xs"></i>
        </span>
        <span>
          La próxima venta programada pasará para el 
          <span class="font-medium text-slate-800">
            {{ formatApiDateOnly(advancePreview()?.nextBillingDate) }}
          </span>
        </span>
      </li>
      <li class="flex gap-3">
        <span
          class="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500"
          aria-hidden="true"
        >
          <i class="pi pi-ban text-xs"></i>
        </span>
        <span>Esta operación no se puede deshacer.</span>
      </li>
    </ul>
  </div>

  <ng-template #footer>
    <div
      class="sale-close-confirm-dialog-footer flex w-full min-w-full max-w-none flex-wrap items-center justify-end gap-2 border-t border-slate-200/80 bg-slate-50 px-5 py-4"
    >
      <p-button
        label="Volver"
        severity="secondary"
        [outlined]="true"
        type="button"
        [disabled]="advanceSubmitting()"
        (onClick)="cancelAdvanceCyclesConfirm()"
      />
      <p-button
        [label]="'Sí, ' + advanceActionLabel().toLowerCase()"
        icon="pi pi-forward"
        severity="warn"
        type="button"
        [loading]="advanceSubmitting()"
        [disabled]="advanceSubmitting()"
        (onClick)="executeAdvanceCycles()"
      />
    </div>
  </ng-template>
</p-dialog>

<p-dialog
  [visible]="catchUpConfirmVisible()"
  (visibleChange)="!$event ? cancelCatchUpConfirm() : catchUpConfirmVisible.set($event)"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [dismissableMask]="!loading()"
  [closable]="!loading()"
  appendTo="body"
  [style]="{ width: 'min(32rem, calc(100vw - 2rem))' }"
  styleClass="sale-close-confirm-dialog"
  contentStyleClass="!p-0 !overflow-hidden rounded-xl"
  [focusOnShow]="true"
>
  <ng-template #header>
    <div class="flex w-full min-w-0 items-start gap-4 pr-2">
      <div
        class="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-100 to-orange-50 text-amber-700 shadow-inner ring-1 ring-amber-200/60"
        aria-hidden="true"
      >
        <i class="pi pi-exclamation-triangle text-xl"></i>
      </div>
      <div class="min-w-0 flex-1 pt-0.5">
        <h2 class="text-lg font-semibold leading-snug tracking-tight text-slate-900">Confirmar ventas por atraso</h2>
        <p class="mt-1 text-sm font-normal text-slate-500">La fecha de inicio de cobro es anterior a hoy</p>
      </div>
    </div>
  </ng-template>

  <div class="border-t border-slate-100 bg-white px-6 py-5">
    <p class="text-sm leading-relaxed text-slate-600">
      Se generarán
      <span class="font-medium text-slate-800">{{ catchUpPreview().catchUpCycleCount }} venta{{ catchUpPreview().catchUpCycleCount === 1 ? '' : 's' }}</span>
      por {{ catchUpPastCyclesDescription() }}
      @if (groupCatchUpCycles()) {
        (agrupadas en una sola venta)
      }
      y, al final, la venta del {{ catchUpCurrentCycleDescription() }}
      @if (catchUpPreview().regularCycleLabel) {
        (<span class="font-medium text-slate-800">{{ catchUpPreview().regularCycleLabel }}</span>).
      } @else {
        .
      }
      Los ciclos vencidos nacerán como vencidas.
    </p>

    <div class="mt-4 max-h-56 overflow-y-auto rounded-lg border border-slate-200">
      <ul class="divide-y divide-slate-100">
        @if (groupCatchUpCycles()) {
          @for (label of catchUpPreview().cycleLabels; track label) {
            <li class="px-4 py-2.5 text-sm text-slate-700">{{ label }}</li>
          }
        } @else {
          @for (sale of catchUpPreviewSales(); track sale.cycleIndex) {
            <li class="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
              <span class="text-slate-700">{{ sale.cycleLabel }}</span>
              <span class="font-medium text-slate-900 tabular-nums">
                {{ sale.total | currency:currencyCode:'symbol':'1.2-2' }}
              </span>
            </li>
          }
        }
      </ul>
    </div>

    <div class="mt-4 flex items-center justify-between rounded-lg bg-slate-50 px-4 py-3 text-sm">
      <span class="font-medium text-slate-700">Total estimado</span>
      <span class="font-semibold text-slate-900 tabular-nums">
        {{ catchUpPreviewTotal() | currency:currencyCode:'symbol':'1.2-2' }}
      </span>
    </div>

    @if (catchUpPreview().nextBillingDate) {
      <p class="mt-4 text-sm text-slate-600">
        La próxima venta programada será el
        <span class="font-medium text-slate-800">{{ catchUpPreview().nextBillingDate | date:'dd/MM/yyyy' }}</span>.
      </p>
    }
  </div>

  <ng-template #footer>
    <div
      class="sale-close-confirm-dialog-footer flex w-full min-w-full max-w-none flex-wrap items-center justify-end gap-2 border-t border-slate-200/80 bg-slate-50 px-5 py-4"
    >
      <p-button
        label="Cancelar"
        severity="secondary"
        [outlined]="true"
        type="button"
        [disabled]="loading()"
        (onClick)="cancelCatchUpConfirm()"
      />
      <p-button
        label="Sí, crear suscripción y ventas"
        icon="pi pi-check"
        severity="warn"
        type="button"
        [loading]="loading()"
        [disabled]="loading()"
        (onClick)="confirmCatchUpAndSubmit()"
      />
    </div>
  </ng-template>
</p-dialog>

<p-dialog
  [visible]="billingCycleChangeVisible()"
  (visibleChange)="!$event ? cancelBillingCycleChange() : billingCycleChangeVisible.set($event)"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [dismissableMask]="!loading()"
  [closable]="!loading()"
  appendTo="body"
  [style]="{ width: 'min(36rem, calc(100vw - 2rem))' }"
  styleClass="sale-close-confirm-dialog"
  contentStyleClass="!p-0 !overflow-hidden rounded-xl"
  [focusOnShow]="true"
>
  @let cyclePreview = billingCycleChangePreview();
  <ng-template #header>
    <div class="flex w-full min-w-0 items-start gap-4 pr-2">
      <div
        class="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-100 to-indigo-50 text-sky-700 shadow-inner ring-1 ring-sky-200/60"
        aria-hidden="true"
      >
        <i class="pi pi-calendar text-xl"></i>
      </div>
      <div class="min-w-0 flex-1 pt-0.5">
        <h2 class="text-lg font-semibold leading-snug tracking-tight text-slate-900">¿Desde cuándo aplica el cambio?</h2>
        <p class="mt-1 text-sm font-normal text-slate-500">Elige cómo se ajustan los cobros de esta suscripción.</p>
      </div>
    </div>
  </ng-template>

  <div class="flex flex-col gap-3 border-t border-slate-100 bg-white px-6 py-5">
    <div class="rounded-lg bg-slate-50 px-4 py-3 text-sm text-slate-700">
      @if (cyclePreview.frequencyChanged) {
        <p>
          Frecuencia de cobro:
          <span class="font-medium text-slate-900">{{ cyclePreview.previousFrequencyLabel }}</span>
          →
          <span class="font-medium text-slate-900">{{ cyclePreview.newFrequencyLabel }}</span>
        </p>
      }
      @if (cyclePreview.billingDayChanged) {
        <p>
          Día de cobro:
          <span class="font-medium text-slate-900">{{ cyclePreview.previousBillingDay ?? 'sin definir' }}</span>
          →
          <span class="font-medium text-slate-900">{{ cyclePreview.newBillingDay }}</span>
        </p>
      }
    </div>

    <button
      type="button"
      class="w-full rounded-lg border px-4 py-3 text-left transition-colors"
      [class.border-sky-500]="billingCycleChangeSelection() === 'ADJUST_CURRENT_PERIOD'"
      [class.bg-sky-50]="billingCycleChangeSelection() === 'ADJUST_CURRENT_PERIOD'"
      [class.border-slate-200]="billingCycleChangeSelection() !== 'ADJUST_CURRENT_PERIOD'"
      [class.opacity-60]="!!cyclePreview.adjustBlockedReason"
      [class.cursor-not-allowed]="!!cyclePreview.adjustBlockedReason"
      [disabled]="!!cyclePreview.adjustBlockedReason || loading()"
      (click)="selectBillingCycleChangeMode('ADJUST_CURRENT_PERIOD')"
    >
      <span class="flex items-center gap-2 text-sm font-semibold text-slate-900">
        <i
          class="pi"
          [class.pi-check-circle]="billingCycleChangeSelection() === 'ADJUST_CURRENT_PERIOD'"
          [class.pi-circle]="billingCycleChangeSelection() !== 'ADJUST_CURRENT_PERIOD'"
        ></i>
        Aplicar desde el periodo actual
      </span>
      <span class="mt-1 block text-sm text-slate-600">
        @if (cyclePreview.adjustBlockedReason || !cyclePreview.currentChargeDate) {
          El cobro del periodo actual se ajusta al nuevo plan.
        } @else if (cyclePreview.currentChargePaid) {
          El cobro del
          <span class="font-medium text-slate-800">{{ cyclePreview.currentChargeDate | date:'dd/MM/yyyy' }}</span>
          por
          <span class="font-medium text-slate-800">{{ cyclePreview.currentChargeTotal | currency:currencyCode:'symbol':'1.2-2' }}</span>
          ya está pagado y cubre el nuevo periodo: no se cobra diferencia.
        } @else {
          El cobro del
          <span class="font-medium text-slate-800">{{ cyclePreview.currentChargeDate | date:'dd/MM/yyyy' }}</span>
          por
          <span class="font-medium text-slate-800">{{ cyclePreview.currentChargeTotal | currency:currencyCode:'symbol':'1.2-2' }}</span>
          se cancela y se emite uno nuevo por
          <span class="font-medium text-slate-800">{{ cyclePreview.newTotal | currency:currencyCode:'symbol':'1.2-2' }}</span>
          con la nueva frecuencia, que cubre del {{ cyclePreview.currentChargeDate | date:'dd/MM/yyyy' }}
          al {{ cyclePreview.adjustedNextBillingDate | date:'dd/MM/yyyy' }}.
        }
        @if (!cyclePreview.adjustBlockedReason && cyclePreview.pastChargesCount > 0) {
          Los {{ cyclePreview.pastChargesCount }} cobros anteriores no cambian.
        }
      </span>
      @if (cyclePreview.adjustBlockedReason) {
        <span class="mt-2 block text-sm font-medium text-amber-700">{{ cyclePreview.adjustBlockedReason }}</span>
      } @else if (cyclePreview.adjustedNextBillingDate) {
        <span class="mt-2 block text-sm text-slate-700">
          Próximo cobro:
          <span class="font-medium text-slate-900">{{ cyclePreview.adjustedNextBillingDate | date:'dd/MM/yyyy' }}</span>
        </span>
      }
    </button>

    <button
      type="button"
      class="w-full rounded-lg border px-4 py-3 text-left transition-colors"
      [class.border-sky-500]="billingCycleChangeSelection() === 'NEXT_CYCLE'"
      [class.bg-sky-50]="billingCycleChangeSelection() === 'NEXT_CYCLE'"
      [class.border-slate-200]="billingCycleChangeSelection() !== 'NEXT_CYCLE'"
      [disabled]="loading()"
      (click)="selectBillingCycleChangeMode('NEXT_CYCLE')"
    >
      <span class="flex items-center gap-2 text-sm font-semibold text-slate-900">
        <i
          class="pi"
          [class.pi-check-circle]="billingCycleChangeSelection() === 'NEXT_CYCLE'"
          [class.pi-circle]="billingCycleChangeSelection() !== 'NEXT_CYCLE'"
        ></i>
        Aplicar a partir del próximo cobro
      </span>
      <span class="mt-1 block text-sm text-slate-600">
        Los cobros que ya se generaron no cambian. El próximo cobro programado se mantiene en su fecha y ya se
        genera {{ cyclePreview.frequencyChanged ? 'con la nueva frecuencia' : 'con el nuevo día de cobro' }}.
      </span>
      @if (cyclePreview.currentNextBillingDate) {
        <span class="mt-2 block text-sm text-slate-700">
          Próximo cobro:
          <span class="font-medium text-slate-900">{{ cyclePreview.currentNextBillingDate | date:'dd/MM/yyyy' }}</span>
          @if (cyclePreview.nextCycleFollowingDate) {
            · después:
            <span class="font-medium text-slate-900">{{ cyclePreview.nextCycleFollowingDate | date:'dd/MM/yyyy' }}</span>
          }
        </span>
      }
      @if (cyclePreview.transitionLonger != null && cyclePreview.currentNextBillingDate && cyclePreview.nextCycleFollowingDate) {
        <span class="mt-2 block text-sm text-amber-700">
          Como cambia el día de cobro, el cobro del {{ cyclePreview.currentNextBillingDate | date:'dd/MM/yyyy' }}
          cubrirá hasta el {{ cyclePreview.nextCycleFollowingDate | date:'dd/MM/yyyy' }}:
          {{ cyclePreview.transitionLonger ? 'unos días más' : 'unos días menos' }} de lo normal.
        </span>
      }
    </button>

    <p class="text-xs text-slate-500">
      Si no estás seguro, elige «Aplicar a partir del próximo cobro»: no modifica los cobros que ya existen.
    </p>
  </div>

  <ng-template #footer>
    <div
      class="sale-close-confirm-dialog-footer flex w-full min-w-full max-w-none flex-wrap items-center justify-end gap-2 border-t border-slate-200/80 bg-slate-50 px-5 py-4"
    >
      <p-button
        label="Volver"
        severity="secondary"
        [outlined]="true"
        type="button"
        [disabled]="loading()"
        (onClick)="cancelBillingCycleChange()"
      />
      <p-button
        label="Guardar cambios"
        icon="pi pi-check"
        type="button"
        [loading]="loading()"
        [disabled]="loading() || !billingCycleChangeSelection()"
        (onClick)="confirmBillingCycleChangeAndSubmit()"
      />
    </div>
  </ng-template>
</p-dialog>

<!-- Modal Agregar/Editar línea -->
@if (lineModalOpen()) {
  <div class="fixed inset-0 z-50 flex items-center justify-center">
    <div
      class="absolute inset-0 bg-black transition-opacity duration-200"
      [class.bg-opacity-40]="lineModalShown()"
      [class.bg-opacity-0]="!lineModalShown()"
      (click)="closeLineModal()"
    ></div>
    <div
      class="relative z-10 flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-xl transform transition-all duration-200"
      [class.opacity-100]="lineModalShown()"
      [class.opacity-0]="!lineModalShown()"
      [class.scale-100]="lineModalShown()"
      [class.scale-95]="!lineModalShown()"
    >
      <div
        class="flex shrink-0 items-center justify-between gap-3 border-b border-slate-200 bg-slate-100 px-4 py-3"
      >
        <h3 class="text-lg font-semibold text-slate-900">
          {{ lineModalMode() === 'create' ? 'Agregar línea' : 'Editar línea' }}
        </h3>
        <p-button icon="pi pi-times" [rounded]="true" [text]="true" severity="secondary" (onClick)="closeLineModal()" type="button" />
      </div>

      <div class="min-h-0 flex-1 overflow-y-auto p-6">
      <form [formGroup]="lineForm">
        <div class="flex flex-col gap-2 mb-4 pb-4 border-b border-slate-200">
          <div class="flex flex-wrap items-center gap-2">
            <span class="line-product-source-select inline-flex">
              <p-selectButton
                name="lineProductSource"
                [options]="lineProductSourceOptions"
                [ngModel]="lineProductSource()"
                [ngModelOptions]="{ standalone: true }"
                (onChange)="onLineProductSourceSelectChange($event)"
                optionLabel="label"
                optionValue="value"
                [allowEmpty]="false"
                [disabled]="lineModalMode() === 'edit'"
              >
                <ng-template pTemplate="item" let-item>
                  <span class="flex items-center justify-center gap-2">
                    @if (lineProductSource() === item.value) {
                      <i class="pi pi-check text-blue-600 text-sm shrink-0" aria-hidden="true"></i>
                    }
                    <span>{{ item.label }}</span>
                  </span>
                </ng-template>
              </p-selectButton>
            </span>
            <i
              class="pi pi-info-circle text-slate-400 cursor-help text-lg shrink-0"
              [pTooltip]="lineProductSourceTooltip"
              [escape]="false"
              tooltipPosition="bottom"
            ></i>
          </div>
          <small class="text-slate-500">
            @if (lineProductSource() === 'CATALOG') {
              Busca un producto del catálogo para precargar datos e impuestos.
            } @else {
              Describe el concepto manualmente; no se vincula a un SKU del inventario.
            }
          </small>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div
            class="flex flex-col gap-1 md:col-span-2 relative rounded-md transition-opacity"
            [class.opacity-60]="lineProductSource() === 'OPEN'"
            [attr.inert]="lineProductSource() === 'OPEN' ? '' : null"
          >
            @if (lineProductSource() === 'OPEN') {
              <div
                class="absolute inset-0 z-20 rounded-md cursor-not-allowed bg-transparent"
                aria-hidden="true"
                (click)="$event.preventDefault(); $event.stopPropagation()"
                (keydown)="$event.preventDefault()"
                tabindex="-1"
              ></div>
            }
            <p-floatlabel class="w-full" variant="on">
              <p-autocomplete
                #productLineRef
                id="productLine"
                formControlName="productSelection"
                [suggestions]="productOptions()"
                (completeMethod)="onProductSearch($event)"
                (onSelect)="onProductSelected($event.value)"
                optionLabel="name"
                placeholder="Busca producto (mín. 2 caracteres)"
                fluid
              />
              <label for="productLine">Producto</label>
            </p-floatlabel>
            <small class="text-slate-500">
              @if (lineProductSource() === 'CATALOG') {
                Selecciona un producto para precargar datos e impuestos.
              } @else {
                No aplica en línea abierta; el producto del catálogo está deshabilitado.
              }
            </small>
          </div>

          <div class="flex flex-col gap-1 md:col-span-2">
            <p-floatlabel class="w-full" variant="on">
              <div class="relative w-full">
                <input pInputText
                  id="lineModalDescription"
                  formControlName="description"
                  [maxlength]="250"
                  fluid
                  class="w-full !pr-9"
                  placeholder="Descripción de la línea"
                  [invalid]="(lineForm.get('description')?.invalid ?? false) && (lineForm.get('description')?.touched ?? false)"
                />
                <button
                  type="button"
                  class="absolute right-2 top-1/2 z-[1] inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md border-0 bg-transparent font-semibold text-sky-700 hover:bg-sky-50 hover:text-sky-900"
                  aria-label="Insertar variable"
                  (click)="openLineDescriptionVarMenu($event)"
                >
                  {{ '@' }}
                </button>
              </div>
              <label for="lineModalDescription">Descripción</label>
            </p-floatlabel>
            @if (lineForm.get('description')?.hasError('required') && lineForm.get('description')?.touched) {
              <small class="text-red-600">La descripción es obligatoria.</small>
            } @else if (lineForm.get('description')?.hasError('maxlength')) {
              <small class="text-red-600">Máximo 250 caracteres.</small>
            } @else {
              <small class="text-slate-500">Personaliza el concepto de la línea</small>
            }
          </div>

          <div class="md:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div class="flex flex-col gap-1">
              <p-floatlabel class="w-full" variant="on">
                <p-inputNumber
                  id="quantity"
                  formControlName="quantity"
                  [min]="0"
                  mode="decimal"
                  locale="en-US"
                  [minFractionDigits]="0"
                  [maxFractionDigits]="4"
                  fluid
                  (onFocus)="selectAllOnFocus($event)"
                  (mouseup)="keepSelectionOnMouseUp($event)"
                  [invalid]="(lineForm.get('quantity')?.invalid ?? false) && (lineForm.get('quantity')?.touched ?? false)"
                />
                <label for="quantity">Cantidad</label>
              </p-floatlabel>
              @if (lineForm.get('quantity')?.hasError('required') && lineForm.get('quantity')?.touched) {
                <small class="text-red-600">La cantidad es obligatoria.</small>
              }
              @if (lineForm.get('quantity')?.hasError('min')) {
                <small class="text-red-600">La cantidad debe ser mayor a 0.</small>
              }
            </div>

            <div class="flex flex-col gap-1">
              <p-floatlabel class="w-full" variant="on">
                <p-inputNumber
                  id="unitPrice"
                  formControlName="unitPrice"
                  [min]="0"
                  mode="decimal"
                  locale="en-US"
                  [minFractionDigits]="2"
                  [maxFractionDigits]="4"
                  [useGrouping]="true"
                  fluid
                  class="[&_input]:text-right"
                  (onFocus)="selectAllOnFocus($event)"
                  (mouseup)="keepSelectionOnMouseUp($event)"
                  [invalid]="(lineForm.get('unitPrice')?.invalid ?? false) && (lineForm.get('unitPrice')?.touched ?? false)"
                />
                <label for="unitPrice">Precio unitario</label>
              </p-floatlabel>
              @if (lineForm.get('unitPrice')?.hasError('required') && lineForm.get('unitPrice')?.touched) {
                <small class="text-red-600">El precio es obligatorio.</small>
              }
              @if (lineForm.get('unitPrice')?.hasError('min') && lineForm.get('unitPrice')?.touched) {
                <small class="text-red-600">El precio debe ser mayor o igual a 0.</small>
              }
            </div>

            <div class="flex flex-col gap-1">
              <p-floatlabel class="w-full" variant="on">
                <p-inputNumber
                  id="lineSubtotal"
                  [ngModel]="lineSubtotal()"
                  [ngModelOptions]="{standalone: true}"
                  mode="currency"
                  currency="USD"
                  locale="en-US"
                  [minFractionDigits]="2"
                  [maxFractionDigits]="4"
                  fluid
                  class="[&_input]:text-right"
                  [readonly]="true"
                  [disabled]="true"
                />
                <label for="lineSubtotal">Subtotal</label>
              </p-floatlabel>
              <small class="text-slate-500">Cantidad × precio unitario.</small>
            </div>
          </div>

          <div class="md:col-span-2 pt-3 mt-1 border-t border-slate-200">
            <button
              type="button"
              class="group inline-flex items-center gap-1.5 rounded px-1 py-1 -ml-1 text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-50 transition-colors"
              (click)="lineFiscalDetailsExpanded.set(!lineFiscalDetailsExpanded())"
              [attr.aria-expanded]="lineFiscalDetailsExpanded()"
            >
              <i
                class="pi text-[0.65rem] text-slate-400 group-hover:text-slate-600"
                [class.pi-chevron-right]="!lineFiscalDetailsExpanded()"
                [class.pi-chevron-down]="lineFiscalDetailsExpanded()"
              ></i>
              <span class="font-medium text-blue-500">Personalizar datos fiscales e impuestos</span>
            </button>
          </div>

          @if (lineFiscalDetailsExpanded()) {
            <div class="md:col-span-2 -mt-1">
              <span class="text-sm font-medium text-slate-800">Datos fiscales</span>
            </div>

            <div class="flex flex-col gap-1">
              <p-floatlabel class="w-full" variant="on">
                <p-autocomplete
                  id="measureUnitLine"
                  formControlName="measureUnitSelection"
                  [suggestions]="measureUnitOptions()"
                  (completeMethod)="onMeasureUnitSearch($event)"
                  (onSelect)="onMeasureUnitSelected($event.value)"
                  optionLabel="name"
                  placeholder="Busca unidad (mín. 2 caracteres)"
                  fluid
                />
                <label for="measureUnitLine">Unidad de medida</label>
              </p-floatlabel>
            </div>

            <div class="flex flex-col gap-1">
              <p-floatlabel class="w-full" variant="on">
                <p-select
                  formControlName="taxExempt"
                  inputId="taxExempt"
                  [options]="taxExemptOptions"
                  optionLabel="label"
                  optionValue="value"
                  class="w-full"
                />
                <label for="taxExempt">Exento de impuestos</label>
              </p-floatlabel>
            </div>

            <div class="flex flex-col gap-1">
              <p-floatlabel class="w-full" variant="on">
                <p-autocomplete
                  id="taxCodeLine"
                  formControlName="taxProductCodeSelection"
                  [suggestions]="taxProductCodeOptions()"
                  (completeMethod)="onTaxProductCodeSearch($event)"
                  (onSelect)="onTaxProductCodeSelected($event.value)"
                  optionLabel="taxDescription"
                  placeholder="Busca código fiscal (mín. 2 caracteres)"
                  fluid
                />
                <label for="taxCodeLine">Código fiscal</label>
              </p-floatlabel>
            </div>

            <div class="flex flex-col gap-1">
              <p-floatlabel class="w-full" variant="on">
                <p-select
                  formControlName="satObjetoImpId"
                  inputId="satObjetoImpLine"
                  [options]="satObjetoImpOptions()"
                  optionLabel="description"
                  optionValue="id"
                  class="w-full"
                  filter
                />
                <label for="satObjetoImpLine">Objeto de impuesto SAT</label>
              </p-floatlabel>
            </div>

            <div class="flex flex-col justify-center">
              <div class="flex items-center gap-2">
                <p-checkbox
                  formControlName="taxInclusive"
                  inputId="taxInclusive"
                  [binary]="true"
                />
                <label for="taxInclusive" class="cursor-pointer select-none">Precio con impuestos incluidos</label>
                <i
                  class="pi pi-info-circle text-slate-400 cursor-help"
                  [pTooltip]="taxInclusiveHelpText"
                  tooltipPosition="top"
                ></i>
              </div>
            </div>
          }
        </div>
      </form>

      @if (lineFiscalDetailsExpanded()) {
        <!-- Impuestos de la línea -->
        <div class="mt-6 border-t border-slate-200 pt-4">
          <div class="flex items-center justify-between mb-3">
            <h4 class="text-sm font-semibold text-slate-700">Impuestos de la línea</h4>
            <p-button label="Agregar impuesto" icon="pi pi-plus" [text]="true" (onClick)="editLineTaxInModal(null)" type="button" />
          </div>
          @if (lineFormTaxes().length === 0) {
            <p class="text-sm text-slate-500">No hay impuestos. Agrega o selecciona un producto.</p>
          } @else {
            <div class="overflow-x-auto">
              <table class="w-full text-sm">
                <thead>
                  <tr class="border-b border-slate-200">
                    <th class="text-left py-2 text-slate-600">Impuesto</th>
                    <th class="text-left py-2 text-slate-600">Tasa</th>
                    <th class="text-left py-2 text-slate-600">Monto</th>
                    <th class="text-left py-2 text-slate-600">Aplicación</th>
                    <th class="text-left py-2 text-slate-600">Tipo de cobro</th>
                    <th class="text-right py-2 text-slate-600">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  @for (t of lineFormTaxes(); track $index) {
                    <tr class="border-b border-slate-100">
                      <td class="py-2">{{ getTaxDisplayWithCode(t.taxCode) }}</td>
                      <td class="py-2">{{ t.taxRate  ?? '-' }}</td>
                      <td class="py-2">{{ t.taxAmount | currency:'USD':'symbol':'1.2-2' ?? '-' }}</td>
                      <td class="py-2">{{ getTaxApplicationLabel(t.applicationType!) }}</td>
                      <td class="py-2">{{ getTaxCollectionTypeLabel(t.collectionType) }}</td>
                      <td class="py-2 text-right">
                        <p-button icon="pi pi-pen-to-square" [rounded]="true" [text]="true" size="small" severity="secondary" (onClick)="editLineTaxInModal($index)" type="button" [pTooltip]="'Editar'" />
                        <p-button icon="pi pi-trash" [rounded]="true" [text]="true" size="small" severity="danger" (onClick)="removeLineTaxInModal($index)" type="button" [pTooltip]="'Eliminar'" />
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }
        </div>
      }

      </div>

      <div
        class="flex shrink-0 justify-end gap-3 border-t border-slate-200 bg-slate-100 px-4 py-3"
      >
        <p-button label="Cancelar" severity="secondary" (onClick)="closeLineModal()" type="button" />
        <p-button
          [label]="lineModalMode() === 'create' ? 'Agregar' : 'Actualizar'"
          icon="pi pi-check"
          [loading]="lineSaving()"
          [disabled]="lineForm.invalid || lineSaving()"
          (onClick)="saveLine()"
          type="button"
        />
      </div>
    </div>
  </div>
}

<p-menu #lineDescriptionVarMenu [model]="lineDescriptionVarMenuItems()" [popup]="true" appendTo="body" />

<!-- Modal Impuesto de línea -->
@if (lineModalOpen() && lineTaxModalOpen()) {
  <div class="fixed inset-0 z-50 flex items-center justify-center">
    <div class="absolute inset-0 bg-black/30" (click)="closeLineTaxModal()"></div>
    <div class="relative bg-white rounded-xl shadow-xl w-full max-w-md p-6 z-50">
      <div class="flex items-center justify-between mb-4">
        <h4 class="font-semibold text-slate-900">
          {{ editingLineTaxIndex() !== null ? 'Editar impuesto' : 'Agregar impuesto' }}
        </h4>
        <p-button icon="pi pi-times" [rounded]="true" [text]="true" severity="secondary" (onClick)="closeLineTaxModal()" type="button" />
      </div>
      <form [formGroup]="lineTaxForm">
        <div class="grid grid-cols-1 gap-4">
          <div class="flex flex-col gap-1">
            <p-floatlabel class="w-full" variant="on">
              <p-select
                formControlName="taxSelection"
                inputId="taxSelection"
                [options]="taxOptions()"
                optionLabel="displayName"
                placeholder="Selecciona impuesto"
                class="w-full"
                [invalid]="(lineTaxForm.get('taxSelection')?.invalid ?? false) && (lineTaxForm.get('taxSelection')?.touched ?? false)"
              />
              <label for="taxSelection">Impuesto</label>
            </p-floatlabel>
          </div>
          <div class="flex flex-col gap-1">
            <p-floatlabel class="w-full" variant="on">
              <p-select
                formControlName="applicationType"
                inputId="applicationType"
                [options]="taxApplicationOptions"
                optionLabel="label"
                optionValue="value"
                class="w-full"
              />
              <label for="applicationType">Tipo de aplicación</label>
            </p-floatlabel>
          </div>
          <div class="flex flex-col gap-1">
            <label class="block text-sm font-medium text-slate-700">Tipo de cobro</label>
            <p-selectButton
              formControlName="collectionType"
              [options]="taxCollectionTypeOptions"
              optionLabel="label"
              optionValue="value"
            >
              <ng-template pTemplate="item" let-item>
                <span class="flex items-center gap-2">
                  @if (lineTaxForm.get('collectionType')?.value === item.value) {
                    <i class="pi pi-check text-sm text-blue-500"></i>
                  }
                  <span>{{ item.label }}</span>
                </span>
              </ng-template>
            </p-selectButton>
            <small class="text-slate-500">Traslado o retención.</small>
          </div>
          <div class="grid grid-cols-2 gap-4">
            <div class="flex flex-col gap-1 min-w-0">
              <p-floatlabel class="w-full" variant="on">
                <p-inputNumber
                  id="taxRateLine"
                  formControlName="taxRate"
                  [min]="0"
                  [max]="100"
                  [maxlength]="2"
                  mode="decimal"
                  [minFractionDigits]="0"
                  [maxFractionDigits]="2"
                  [allowEmpty]="true"
                  fluid
                />
                <label for="taxRateLine">Tasa (%)</label>
              </p-floatlabel>
              <small class="text-slate-500">% de impuestos</small>
            </div>
            <div class="flex flex-col gap-1 min-w-0">
              <p-floatlabel class="w-full" variant="on">
                <p-inputNumber
                  id="taxAmountLine"
                  formControlName="taxAmount"
                  [min]="0"
                  mode="decimal"
                  inputStyleClass="text-right"
                  [minFractionDigits]="0"
                  [maxFractionDigits]="4"
                  [allowEmpty]="true"
                  fluid
                />
                <label for="taxAmountLine">Monto</label>
              </p-floatlabel>
              <small class="text-slate-500">Cuota de impuesto</small>
            </div>
          </div>
          <div class="flex flex-col gap-1">
            <p-floatlabel class="w-full" variant="on">
              <p-inputNumber
                id="taxBaseLine"
                formControlName="taxBase"
                [min]="0"
                mode="decimal"
                [minFractionDigits]="0"
                [maxFractionDigits]="2"
                fluid
                placeholder="0"
              />
              <label for="taxBaseLine">Base</label>
            </p-floatlabel>
          </div>
        </div>
      </form>
      <div class="flex justify-end gap-3 mt-6">
        <p-button label="Cancelar" severity="secondary" (onClick)="closeLineTaxModal()" type="button" />
        <p-button label="Guardar" icon="pi pi-check" [disabled]="lineTaxForm.invalid" (onClick)="saveLineTaxInModal()" type="button" />
      </div>
    </div>
  </div>
}

<p-drawer
  [visible]="isSaleDetailsOpen()"
  position="right"
  (visibleChange)="onSaleDrawerVisibleChange($event)"
  styleClass="subscription-sale-drawer !w-[800px]"
  [blockScroll]="true">
  <ng-template pTemplate="header">
    <div class="flex items-center justify-between w-full gap-3">
      <div class="flex items-center gap-3 min-w-0">
        <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-violet-100 text-violet-600 shadow-sm">
          <i class="pi pi-shopping-cart text-base"></i>
        </span>
        <h3 class="text-lg font-semibold text-slate-900">Detalle de venta</h3>
      </div>
      @if (selectedSale() && !saleDetailsLoading()) {
        <button
          type="button"
          class="inline-flex items-center gap-2 text-sm font-semibold bg-blue-600 text-white px-3 py-2 rounded-lg shadow-sm hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 shrink-0"
          (click)="editSaleFromModal()">
          <i class="pi pi-pencil text-sm"></i>
          Editar
        </button>
      }
    </div>
  </ng-template>
  @if (saleDetailsLoading()) {
    <div class="flex flex-col items-center justify-center py-12 gap-4">
      <i class="pi pi-spin pi-spinner text-4xl text-sky-500"></i>
      <p class="text-sm text-slate-600">Cargando venta...</p>
    </div>
  } @else if (selectedSale()) {
    <app-sale-read
      [sale]="selectedSale()!"
      [charges]="saleCharges()"
      (openCustomer)="openCustomerDetailsById($event)"
      (openSubscription)="onSaleReadOpenSubscription($event)" />
  }
</p-drawer>

<p-drawer
  [visible]="isCustomerDetailsOpen()"
  position="right"
  (visibleChange)="onCustomerDrawerVisibleChange($event)"
  styleClass="customer-details-drawer !w-[800px]"
  [blockScroll]="true">
  <ng-template pTemplate="header">
    <div class="flex items-center justify-between w-full gap-3">
      <div class="flex items-center gap-3 min-w-0">
        <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-600 shadow-sm">
          <i class="pi pi-user text-base"></i>
        </span>
        <h3 class="text-lg font-semibold text-slate-900">Detalles del cliente</h3>
      </div>
      @if (selectedCustomer() && !customerDetailsLoading() && !isCustomerLocked()) {
        <button
          type="button"
          class="inline-flex items-center gap-2 text-sm font-semibold bg-blue-600 text-white px-3 py-2 rounded-lg shadow-sm hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 shrink-0"
          (click)="editCustomerFromModal()">
          <i class="pi pi-pencil text-sm"></i>
          Editar
        </button>
      }
    </div>
  </ng-template>
  @if (customerDetailsLoading()) {
    <div class="flex flex-col items-center justify-center py-12 gap-4">
      <i class="pi pi-spin pi-spinner text-4xl text-sky-500"></i>
      <p class="text-sm text-slate-600">Cargando cliente...</p>
    </div>
  } @else if (selectedCustomer()) {
    <app-customer-read
      [customer]="selectedCustomer()!"
      (openSale)="openSaleFromCustomer($event)" />
  }
</p-drawer>

